import React, { useState, useCallback, useMemo, useEffect, useRef, useReducer } from 'react';
import { useStore, useNav } from '../../store/StoreContext';
import { useAuth } from '../../auth/AuthContext';
import { format, addDays, isToday, startOfWeek, parseISO, isSameDay } from 'date-fns';
import { IconChevronLeft, IconChevronRight, IconEdit, IconRefresh, IconX } from '../icons';
import { WeeklyScheduleModal, DailyDispatchModal, CrewReorderModal, DispatchPillExpanded, DispatchChangesModal } from './index';
import { jobTentativeFlag, jobDateKey } from './DispatchPillExpanded';
import AssignModal from '../modals/AssignModal';
import HistoryModal from '../modals/HistoryModal';
import { useConfirm } from '../../hooks/useConfirm';
import JobModal from '../modals/JobModal';
import { Modal, FormField, StatusPicker, HelpTip, DatePicker, Combobox, PopoverPanel, Button, IconButton, ChoiceKeys } from '../ui';
import './dispatch-width.css';
import { useToast } from '../../hooks/useToast';
import { formatTime12 } from '../../utils/formatters';
import { getTripTag, getLegCrew, getCrewSkillSummary, transferTruckCovers } from '../../utils/dispatchHelpers';
import { kindBadge, kindLetter, kindSortKey, kindSegments, kindInTruckChain, kindIsAnchor, kindIsHoldOnly, kindLoadsTruck } from '../../lib/phaseKinds';
import { crewOffInfo } from '../../lib/crewOff';
import { QUICK_DISPATCH_TYPES, truckSizeShortLabel, estimateExtraTrucks } from '../../lib/constants';
import QuickDispatchModal from '../modals/QuickDispatchModal';
import db from '../../lib/database';
import { supabase } from '../../lib/supabase';

const JOB_TYPES = ['LOCAL', 'LONG_DISTANCE', 'STORAGE'];
const CREW_COLORS = {
  'red': '#dc2626',
  'blue': '#2563eb',
  'green': '#16a34a',
  'purple': '#9333ea'
};
// Legacy trip-planner SPAN_TAGS/SPAN_TAG_COLORS removed in the Phase Planner
// merge — multi-day out-of-state trips are now expressed as separate phase
// rows (pack/load/drive/unload/return/hold) that each become their own
// move_jobs pill. The `trip_tags` JSONB column on move_jobs is left intact so
// legacy spanning rows that were already saved still render via the
// getTripTag/getLegCrew helpers further down.
//
// Phase badge colors/labels/letters are generated from the kind's
// segments by src/lib/phaseKinds.js (2026-08-26 combo refactor) — the
// hand-maintained PHASE_KIND_BADGE / PHASE_KIND_LETTER maps are gone, so
// any combo the planner produces ('drive+unload+return') renders with a
// coherent badge automatically.

// Compact crew label: "Julian Beaty" → "Julian B.". Single-name
// entries pass through unchanged ("Jensen" → "Jensen"). Used in the
// crew row header on the dispatch board to free up horizontal room
// without making the row anonymous — first name still front and center.
const crewShortName = (fullName) => {
  if (!fullName) return '?';
  const parts = String(fullName).trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  return `${parts[0]} ${last[0]}.`;
};


const buttonStyles = {
  inputCls: 'border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full'
};

const todayMonday = (() => {
  const now = new Date();
  const day = now.getDay();
  const diff = now.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(now.setDate(diff));
})();

function getStateAbbr(address) {
  if (!address) return null;
  const match = address.match(/,\s*([A-Z]{2})\s*\d{5}(?:-\d{4})?\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*\d{0,5}\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*$/)
    || address.match(/,\s*([A-Z]{2})\s*,/)
    || address.match(/\b([A-Z]{2})\s+\d{5}/);
  return match ? match[1] : null;
}

// Dispatch view token helpers were removed 2026-05-14 — they were defined
// here as dead copy-paste from DailyDispatchModal and the secret was
// hardcoded, leaking it via the frontend bundle. Live mint flow lives in
// DailyDispatchModal.jsx → POST /api/dispatch-token. See security audit
// notes in BBM Context for claude/security-backlog.md.

function getCrewPortalUrl(portalToken) {
  const base = window.location.origin;
  return `${base}/crew?token=${portalToken}`;
}

function buildCrewLeadDispatch(dayJobs, crew, clients, trucks, dispatchLinks, portalLink) {
  const crewOnJobs = new Set();
  dayJobs.forEach(j => (j.crew_ids || []).forEach(id => { if (id !== j.leader_id) crewOnJobs.add(id); }));
  const crewNames = [...crewOnJobs].map(id => crew.find(c => c.id === id)?.name).filter(Boolean);
  const allTruckIds = [...new Set(dayJobs.flatMap(j => j.truck_ids || []))];
  const truckNames = allTruckIds.map(id => trucks.find(t => t.id === id)?.name).filter(Boolean);

  let msg = `BEATY MOVING — Tomorrow's Jobs\n\n`;
  if (crewNames.length) msg += `Hi, tomorrow you have ${crewNames.join(', ')}.`;
  if (truckNames.length) msg += ` Pls take ${truckNames.join(' and ')}.`;
  msg += '\n';

  dayJobs.sort((a, b) => (a.day_sequence || 1) - (b.day_sequence || 1));

  dayJobs.forEach((job, idx) => {
    const client = clients.find(c => c.id === job.client_id);
    msg += `\n********\n`;
    if (dayJobs.length > 1) msg += `${idx + 1}${idx === 0 ? 'st' : idx === 1 ? 'nd' : idx === 2 ? 'rd' : 'th'} Job: `;
    else msg += `Job: `;
    msg += `${job.job_label || job.type.replace('_', ' ')}\n\n`;
    msg += `   ${client?.name || 'TBD'}\n`;
    if (job.location_name) msg += `   ${job.location_name}\n`;
    msg += `   ${job.from_address || 'TBD'}\n`;
    const jobClient = clients.find(c => c.id === job.client_id);
    if (jobClient?.additional_stops) {
      try {
        const stops = JSON.parse(jobClient.additional_stops);
        if (Array.isArray(stops)) stops.forEach((s, si) => {
          if (s.address) msg += `Stop ${si + 2}:\n   ${s.address}\n`;
        });
      } catch(e) {}
    }
    msg += `Move TO:\n`;
    msg += `   ${job.to_address || 'TBD'}\n`;
    if (job.heavy_items) msg += `\n${job.heavy_items}\n`;
    if (job.special_notes) msg += `${job.special_notes}\n`;
    if (job.dispatcher_notes) msg += `${job.dispatcher_notes}\n`;
    if (job.stairs) msg += `Has stairs (${job.levels || '?'} levels)\n`;
  });

  if (portalLink) {
    msg += `\n━━━━━━━━\nYour Daily Portal:\n${portalLink}\n(View all jobs, photos, update status & submit recaps)\n`;
  }

  return msg;
}

function buildCrewMemberDispatch(dayJobs, memberId, crew, clients, trucks) {
  const leader = crew.find(c => c.id === dayJobs[0]?.leader_id);
  let msg = `BEATY MOVING — Tomorrow\n\n`;
  msg += `${dayJobs[0]?.time || '8:00'} — ${dayJobs[0]?.from_address || 'TBD'}\n`;
  if (leader) msg += `Lead: ${leader.name}\n`;
  const otherCrew = [...new Set(dayJobs.flatMap(j => j.crew_ids || []))].filter(id => id !== memberId).map(id => crew.find(c => c.id === id)?.name).filter(Boolean);
  if (otherCrew.length) msg += `With: ${otherCrew.join(', ')}\n`;
  msg += `\nSee ${leader?.name || 'your crew lead'} for details on-site.`;
  return msg;
}

async function dispatchJobSMS(job, clients, crew, trucks) {
  const client = clients.find(c => c.id === job.client_id);
  const messages = [];

  for (const crewId of (job.crew_ids || [])) {
    const member = crew.find(c => c.id === crewId);
    if (!member?.phone) continue;

    const isLeader = crewId === job.leader_id;
    const body = isLeader
      ? buildCrewLeadMessage(job, client, crew, trucks)
      : buildCrewMemberMessage(job, client, crew, trucks, member.name);

    messages.push({ to: member.phone, body });
  }

  if (messages.length === 0) throw new Error('No crew members with phone numbers to notify');

  let response;
  try {
    response = await fetch('/api/dispatch-sms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ messages }),
    });
  } catch (fetchErr) {
    throw new Error('Network error — could not reach SMS server. Check your connection.');
  }

  let data;
  try {
    data = await response.json();
  } catch (parseErr) {
    const text = await response.text().catch(() => 'unknown');
    throw new Error(`Server returned invalid response (${response.status}): ${text.substring(0, 100)}`);
  }

  if (!response.ok) throw new Error(data.error || `SMS dispatch failed (HTTP ${response.status})`);
  return data;
}

function buildCrewLeadMessage(job, client, crew, trucks) {
  const crewNames = (job.crew_ids || []).filter(id => id !== job.leader_id).map(id => crew.find(c => c.id === id)?.name).filter(Boolean);
  const truckNames = (job.truck_ids || []).map(id => trucks.find(t => t.id === id)?.name).filter(Boolean);
  let msg = `BEATY MOVING — Job Assignment\n\n`;
  if (crewNames.length) msg += `Hi, tomorrow you have ${crewNames.join(', ')}.`;
  if (truckNames.length) msg += ` Pls take ${truckNames.join(' and ')}.`;
  msg += `\n\n********\n`;
  msg += `Job: ${job.job_label || job.type.replace('_', ' ')}\n\n`;
  msg += `   ${client?.name || 'TBD'}\n`;
  if (job.location_name) msg += `   ${job.location_name}\n`;
  msg += `   ${job.from_address || 'TBD'}\n`;
  if (client?.additional_stops) {
    try {
      const stops = JSON.parse(client.additional_stops);
      if (Array.isArray(stops)) stops.forEach((s, si) => {
        if (s.address) msg += `Stop ${si + 2}:\n   ${s.address}\n`;
      });
    } catch(e) {}
  }
  msg += `Move TO:\n`;
  msg += `   ${job.to_address || 'TBD'}\n`;
  if (job.heavy_items) msg += `\n${job.heavy_items}\n`;
  if (job.special_notes) msg += `${job.special_notes}\n`;
  if (job.dispatcher_notes) msg += `${job.dispatcher_notes}\n`;
  if (job.stairs) msg += `Has stairs (${job.levels || '?'} levels)\n`;
  return msg;
}

function buildCrewMemberMessage(job, client, crew, trucks, memberName) {
  const leader = crew.find(c => c.id === job.leader_id);
  const otherCrew = (job.crew_ids || []).map(id => crew.find(c => c.id === id)?.name).filter(Boolean);
  let msg = `BEATY MOVING — Tomorrow\n\n`;
  msg += `${job.time || '8:00'} — ${job.from_address || 'TBD'}\n`;
  if (leader) msg += `Lead: ${leader.name}\n`;
  msg += `With: ${otherCrew.join(', ')}\n`;
  msg += `\nSee ${leader?.name || 'your crew lead'} for details on-site.`;
  return msg;
}

export default function DispatchBoard({ viewSwitcher = null }) {
  const { crew, trucks, clients, jobs, estimates, moves, dayFlags, availability, saveJob, removeJob, saveEstimate, saveMove, patchClient, saveCrew, setCrewAvailability, reorderCrew, undoStack, undo, redoStack, redo, restoreSnapshot, getSnapshots } = useStore();

  // Moves (2026-09-18, rule 3): a pill whose move date is still tentative
  // wears a dashed amber edge. One Map over `moves` so the per-pill lookup
  // is O(1); jobs without a move_id (legacy rows) are never marked.
  const movesById = useMemo(() => new Map((moves || []).map(m => [m.id, m])), [moves]);
  const tentativeFor = useCallback((job) => job?.move_id ? jobTentativeFlag(job, movesById.get(job.move_id)) : false, [movesById]);
  // Day flags (day_flags table): FULL / CLOSED chip under the day label.
  // Read-only here — edited on the Capacity page.
  const flagByDate = useMemo(() => new Map((dayFlags || []).map(f => [f.date, f])), [dayFlags]);
  const nav = useNav();
  const [weekOffset, setWeekOffset] = useState(0);
  const [expandedPill, setExpandedPill] = useState(null);
  const { show: showToast } = useToast();
  const [assignModal, setAssignModal] = useState(null);
  const [jobModal, setJobModal] = useState(null);
  // Quick Dispatch (2026-08-06): { client, date } — small-job fast path
  // for the NEW (no-estimate) panel cards of quick-type clients.
  const [quickModal, setQuickModal] = useState(null);
  // Portal Preview (2026-08-06): { crewId, date } — toolbar picker for
  // opening a read-only copy of a crew member's portal in a new tab.
  const [portalPreview, setPortalPreview] = useState(null);
  // Past-walkthroughs resolve list (2026-08-07 round 3).
  const [pastWtModal, setPastWtModal] = useState(false);
  // Pill hover card (Jaylen 2026-08-07): instant crew + truck summary on
  // hover — per PILL, not per cell, because one lead can run different
  // trucks on different jobs in the same day. {job, dateStr, x, y, alignRight}.
  // Fixed-positioned at the pill's edge (clamped to the viewport) so cell
  // overflow/stacking contexts can't clip it. Cleared on drag + click
  // (the click popover is the richer surface).
  const [hoverPill, setHoverPill] = useState(null);
  // Past unassigned walkthroughs (Jaylen 2026-08-07): an unassigned
  // walkthrough is a NORMAL state ("one of the 3 of us will do it") —
  // but once its date passes, nobody has told the system whether it
  // happened. One-click resolve: ✓ It Happened runs the unified complete
  // (job row + the client's walkthrough fields, same as the estimator
  // app / pill popup); rescheduling stays a drag to a new day.
  const resolvePastWalkthrough = async (job) => {
    try {
      await saveJob({ ...job, status: 'completed' });
      const cl = getClient(job.client_id);
      if (cl?.id && cl.walkthrough_status !== 'completed') {
        // patchClient, NOT saveClient — partial rows through the full
        // upsert hit the NOT NULL insert path (store comment on
        // patchClient; this threw the null toast on first ship).
        await patchClient(cl.id, {
          walkthrough_status: 'completed',
          walkthrough_completed_at: new Date().toISOString(),
          walkthrough_completed_by: cl.walkthrough_completed_by || 'Office (dispatch board)',
        });
      }
      showToast(`Walkthrough marked done — ${cl?.name || 'client'}`);
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
  };

  const showHoverCard = (e, job, dateStr) => {
    const r = e.currentTarget.getBoundingClientRect();
    const alignRight = r.right + 270 > window.innerWidth;
    setHoverPill({
      job,
      dateStr,
      x: alignRight ? r.left - 268 : r.right + 8,
      y: Math.min(r.top, window.innerHeight - 190),
      alignRight,
    });
  };
  const openPortalPreview = (crewId, dateIso) => {
    if (!crewId) return;
    const d = dateIso || format(new Date(), 'yyyy-MM-dd');
    window.open(`/crew?preview_crew=${encodeURIComponent(crewId)}&preview_date=${encodeURIComponent(d)}`, '_blank', 'noopener');
  };
  const [defaultDate, setDefaultDate] = useState(null);
  const [smsConfirm, setSmsConfirm] = useState(null);
  const [smsSending, setSmsSending] = useState(false);
  const [cellMenu, setCellMenu] = useState(null);
  const [weeklyModal, setWeeklyModal] = useState(false);
  const [dailyModal, setDailyModal] = useState(null);
  const [quickText, setQuickText] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [historyModal, setHistoryModal] = useState(false);
  // In-app confirm (QA M2 NEW-9 — native confirms freeze the board
  // invisibly under automation and some real setups).
  const { confirm: confirmDialog, confirmElement } = useConfirm();
  const boardRef = useRef(null);
  // ── Narrow-width behavior (BBM Context for claude/dispatch-width.md) ──
  // The board measures itself. Under 1100px the Unassigned rail folds
  // into a toolbar button + popover (aisle 2, pick A). At 1wk the day
  // columns never drop below 122px — the grid scrolls sideways with the
  // crew column pinned — and the pills step down in tiers keyed to the
  // column width they actually get (aisle 1, pick B). The swap to Day
  // view under 560px lives in DispatchPage.
  const [boardWidth, setBoardWidth] = useState(0);
  const [railOpen, setRailOpen] = useState(false);
  const railBtnRef = useRef(null);
  // Callback ref: the board div isn't in the tree on first mount (loading
  // state renders first), so a mount-only effect would observe nothing.
  const [boardEl, setBoardEl] = useState(null);
  const boardRefCb = useCallback((el) => { boardRef.current = el; setBoardEl(el); }, []);
  useEffect(() => {
    if (!boardEl || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width || 0;
      setBoardWidth(prev => (Math.abs(prev - w) > 2 ? w : prev));
    });
    ro.observe(boardEl);
    return () => ro.disconnect();
  }, [boardEl]);
  const [crewReorderModal, setCrewReorderModal] = useState(false);
  const [dragJob, setDragJob] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [dateConfirm, setDateConfirm] = useState(null);
  // Which trip groups (keyed by estimate_id) are expanded in the right-side
  // unassigned pool. Collapsed by default — the dispatcher clicks a client's
  // card to see the individual legs and optionally drag a single leg.
  const [expandedTripIds, setExpandedTripIds] = useState(() => new Set());

  // Local-midnight timestamp for "today" used by the dispatch-window
  // check below (today + tomorrow only). Refreshed every render — cheap
  // and keeps the comparison correct across midnight transitions while
  // the board is left open.
  const todayDateOnly = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  }, []);


  // Per-day daily-dispatch state (sql/039 + sql/074).
  //   pendingByDate     — { 'YYYY-MM-DD': pendingPushCount } for dates
  //                       that have NOT been dispatched yet. Surfaces
  //                       as a count badge on the toolbar's 📋 Daily
  //                       Dispatch button (2026-07-29 reconciliation:
  //                       the toolbar is the only day-send entry now).
  //   dispatchedDates   — Set of date ISOs already released. Day
  //                       columns for these dates show a passive
  //                       "✓ Sent" state.
  //   changesByDate     — { 'YYYY-MM-DD': openChangeCount } from
  //                       dispatch_change_log: office edits made AFTER
  //                       the day's SMS went out. Turns "✓ Sent" into
  //                       the amber "↻ N changed" chip.
  const [pendingByDate, setPendingByDate] = useState({});
  const [dispatchedDates, setDispatchedDates] = useState(() => new Set());
  const [changesByDate, setChangesByDate] = useState({});
  // Date ISO whose changes-since-send panel is open (null = closed).
  const [changesModal, setChangesModal] = useState(null);

  // Refetch the daily-dispatch + pending + changes counts. Cheap (three
  // small queries) and idempotent — called on mount, after daily-dispatch
  // sends, and on a slow poll so the badge stays roughly current
  // without coupling to every saveJob path.
  const refreshDispatchGate = async () => {
    try {
      const [counts, sentRows, changeCounts] = await Promise.all([
        db.getPendingPushCountsByDate(),
        supabase.from('daily_dispatch_sent').select('move_date'),
        db.getDispatchChangeCounts(),
      ]);
      setPendingByDate(counts || {});
      const set = new Set();
      for (const r of (sentRows?.data || [])) set.add(r.move_date);
      setDispatchedDates(set);
      setChangesByDate(changeCounts || {});
    } catch (e) {
      // Non-fatal — the dispatch board still works without this gate
      // info, the dispatcher just loses the badge visibility.
      console.warn('refreshDispatchGate failed:', e?.message || e);
    }
  };
  useEffect(() => {
    refreshDispatchGate();
    // Poll every 90s. The cron flushes every 60s, so this keeps the
    // dispatcher's view roughly aligned without hammering the DB.
    const handle = setInterval(refreshDispatchGate, 90 * 1000);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Zoom level: how many weeks the board shows at once (1–4). Default 1.
  const [zoomWeeks, setZoomWeeks] = useState(1);
  // When dragging near the right edge the board auto-expands to 2 weeks;
  // this ref tracks whether the expansion was automatic so we can snap back.
  const autoExpandedRef = useRef(false);

  // TV mode is an explicit toggle (persisted per device). When on at
  // 2wk+ it strips the toolbar chrome and the right Unassigned panel
  // so the board takes the full screen. The auto-detect attempt was
  // unreliable on smart-TV browsers reporting weird viewport widths,
  // so it's just a manual switch now.
  const [tvMode, setTvMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem('bbm-dispatch-tv-mode') === 'on';
    } catch (e) {
      return false;
    }
  });
  const toggleTvMode = () => {
    setTvMode(prev => {
      const next = !prev;
      try {
        if (next) window.localStorage.setItem('bbm-dispatch-tv-mode', 'on');
        else window.localStorage.removeItem('bbm-dispatch-tv-mode');
      } catch (e) { /* localStorage may be blocked */ }
      return next;
    });
  };
  // The TV layout treatment (toolbar + right sidebar trimmed for max
  // board real estate) kicks in at 2wk and above when TV mode is on.
  // The pill body's extra-compact rendering only changes at 3wk+ where
  // columns get genuinely tight — at 2wk they're still ~110px wide.
  const isTvLayoutActive = zoomWeeks >= 2 && tvMode;
  const narrowRail = !isTvLayoutActive && boardWidth > 0 && boardWidth < 1100;
  // Column width the 1wk grid will actually give each day: board minus
  // padding, minus the rail when it's showing, minus the crew column,
  // over 7 — floored at 122 (the CSS minmax floor). Tiers: 1 under 160
  // (type label goes), 2 under 136 (tags → letter, trucks → dots).
  // Under 560px even three floored columns don't fit (160 + 3×122 + padding),
  // so the 1wk board shows ONE day at a time — same grid, same pills, same
  // drag/drop, just a single day column plus a Mon–Sun strip to hop days.
  // (Jaylen 2026-09-11: the timeline Day view is a different animal; this
  // is the board itself, narrowed.) The week returns the moment the window
  // grows.
  const narrowDay = !isTvLayoutActive && zoomWeeks === 1 && boardWidth > 0 && boardWidth < 560;
  const [narrowDayIdx, setNarrowDayIdx] = useState(null);
  // Column floor per zoom (px). 1wk pills were built for ~130; 2wk pills
  // (first name + time) for ~100; 3wk/4wk are plain capacity blocks and
  // only need to stay visible. Below the floor the grid scrolls sideways.
  const COL_FLOOR = { 1: 122, 2: 84, 3: 44, 4: 34 };
  const colFloor = COL_FLOOR[zoomWeeks] || 34;
  const floorActive = !narrowDay;
  const crewColW = zoomWeeks >= 3 ? 90 : zoomWeeks >= 2 ? 110 : 160;
  // Rail width flexes with the board before it folds away: 288px on a
  // wide screen, down to 200px just above the 1100 fold.
  const railW = boardWidth > 0 ? Math.round(Math.min(288, Math.max(200, boardWidth * 0.22))) : 288;
  const dayColWidth = (() => {
    if (!floorActive || boardWidth <= 0) return 999;
    const pad = 48, rail = (narrowRail || isTvLayoutActive) ? 0 : railW + 24;
    return Math.max(colFloor, (boardWidth - pad - rail - crewColW) / (zoomWeeks * 7));
  })();
  // Tiers are relative to what each zoom's pill was built for:
  //  1wk: <160 → 1 (type label off), <136 → 2 (tags → letter, trucks → dots)
  //  2wk: <110 → 2, <96 → 3 (also drops the state chip, "Walkthrough" → "WT")
  const pillTier = !floorActive ? 0
    : zoomWeeks === 1 ? (dayColWidth < 136 ? 2 : dayColWidth < 160 ? 1 : 0)
    : zoomWeeks === 2 ? (dayColWidth < 96 ? 3 : dayColWidth < 110 ? 2 : 0)
    : 0;

  useEffect(() => {
    if (nav?.dispatchFocusDate) {
      const focusDate = parseISO(nav.dispatchFocusDate);
      const focusWeekStart = startOfWeek(focusDate, { weekStartsOn: 1 });
      const diffMs = focusWeekStart.getTime() - todayMonday.getTime();
      const diffWeeks = Math.round(diffMs / (7 * 24 * 60 * 60 * 1000));
      setWeekOffset(diffWeeks);
      nav.clearDispatchFocus();
    }
  }, [nav?.dispatchFocusDate]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      boardRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Auto-disable TV mode whenever fullscreen exits. TV mode strips the
  // chrome (toolbar, sidebar) for a big-display, full-attention view —
  // if the dispatcher escapes back to a normal browser window we don't
  // want them stuck in a half-broken-looking dispatch board with the
  // toolbar still hidden.
  useEffect(() => {
    if (!isFullscreen && tvMode) {
      setTvMode(false);
      try { window.localStorage.removeItem('bbm-dispatch-tv-mode'); } catch (e) {}
    }
  }, [isFullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!cellMenu) return;
    const handler = () => setCellMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [cellMenu]);

  const weekStart = addDays(todayMonday, weekOffset * 7);
  const weekDays = Array.from({length: zoomWeeks * 7}, (_, i) => addDays(weekStart, i));
  const baseDayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const todayIdx = weekDays.findIndex(d => isToday(d));
  const narrowIdx = narrowDayIdx !== null && narrowDayIdx < weekDays.length ? narrowDayIdx : (todayIdx >= 0 ? todayIdx : 0);
  const days = narrowDay ? [weekDays[narrowIdx]] : weekDays;
  const totalDays = days.length;
  const dayLabels = narrowDay ? [baseDayLabels[narrowIdx % 7]] : days.map((_, i) => baseDayLabels[i % 7]);

  const weekDates = days.map(d => format(d, 'yyyy-MM-dd'));
  const fullWeekDates = weekDays.map(d => format(d, 'yyyy-MM-dd')); // modals always get the whole week, even in single-day mode

  const getJobsForCrewDay = (crewId, dateStr) => {
    const directJobs = jobs.filter(j => j.move_date === dateStr && (j.crew_ids || []).includes(crewId));
    const spanningJobs = jobs.filter(j => {
      if (!j.move_date || (j.duration_days || 1) <= 1) return false;
      if (j.move_date >= dateStr) return false;
      const startDate = parseISO(j.move_date);
      const endDate = addDays(startDate, (j.duration_days || 1) - 1);
      const cellDate = parseISO(dateStr);
      if (cellDate > endDate) return false;
      const legCrew = getLegCrew(j.trip_tags, dateStr);
      if (legCrew && legCrew.length > 0) {
        return legCrew.includes(crewId);
      }
      const anyLegCrew = j.trip_tags ? Object.values(j.trip_tags).find(v => typeof v === 'object' && v.leg_crew_ids && v.leg_crew_ids.length > 0)?.leg_crew_ids : null;
      if (anyLegCrew) {
        return anyLegCrew.includes(crewId);
      }
      return (j.crew_ids || []).includes(crewId);
    }).map(j => ({ ...j, _isSpanDay: true, _spanDayNum: Math.round((parseISO(dateStr) - parseISO(j.move_date)) / (86400000)) + 1 }));
    return [...directJobs, ...spanningJobs].sort((a, b) => (a.day_sequence || 1) - (b.day_sequence || 1));
  };

  const getAvail = (crewId, dateStr) => {
    return availability.find(a => a.crew_id === crewId && a.date === dateStr);
  };

  const getCrew = (id) => crew.find(c => c.id === id);
  const getTruck = (id) => trucks.find(t => t.id === id);
  const getClient = (id) => clients.find(c => c.id === id);

  // Truck availability: compute which trucks are committed on each day.
  // A truck assigned to a job on a given date is unavailable for other jobs.
  // Returns a Map<dateStr, Map<truckId, jobId[]>> for quick lookups.
  const truckBookings = useMemo(() => {
    const bookings = new Map();
    const addBooking = (dateStr, truckId, jobId, leaderId) => {
      if (!bookings.has(dateStr)) bookings.set(dateStr, new Map());
      const dayMap = bookings.get(dateStr);
      if (!dayMap.has(truckId)) dayMap.set(truckId, []);
      dayMap.get(truckId).push({ jobId, leaderId });
    };
    for (const j of jobs) {
      if (!j.move_date || !j.truck_ids || j.truck_ids.length === 0) continue;
      if (j.status === 'unassigned' && (j.crew_ids || []).length === 0) continue;
      for (const tid of j.truck_ids) {
        addBooking(j.move_date, tid, j.id, j.leader_id || null);
      }
    }
    return bookings;
  }, [jobs]);

  // Check if a truck is double-booked on a given date. SAME-LEAD RULE
  // (Jaylen 2026-08-07): one lead running the same truck across his own
  // jobs all day is the normal small-job pattern, not a conflict — only
  // flag when the truck's other jobs belong to a DIFFERENT lead (or to
  // no lead, where ownership is unknowable).
  const isTruckConflict = (truckId, dateStr, jobId, leaderId) => {
    const dayMap = truckBookings.get(dateStr);
    if (!dayMap) return false;
    const others = (dayMap.get(truckId) || []).filter(b => b.jobId !== jobId);
    if (others.length === 0) return false;
    return others.some(b => !leaderId || !b.leaderId || b.leaderId !== leaderId);
  };

  const handleCellClick = (crewId, dateStr) => {
    setCellMenu({ crewId, dateStr });
  };

  const handleCellRightClick = (e, crewId, dateStr) => {
    e.preventDefault();
    setCellMenu({ crewId, dateStr });
  };

  const handleSetAvailability = async (crewId, dateStr, status) => {
    try {
      await setCrewAvailability(crewId, dateStr, status);
      setCellMenu(null);
      if (status) showToast(status === 'off_requested' ? 'Marked OFF' : status === 'scheduled_off' ? 'Marked Scheduled Off' : status === 'on_call' ? 'Marked On Call' : 'Marked Office');
      else showToast('Cleared');
    } catch (e) { showToast('Error: ' + e.message, 'error'); }
  };

  const handleSaveJob = async (form, deleteId) => {
    if (deleteId) { await removeJob(deleteId); showToast('Job deleted'); return; }
    await saveJob(form);
    showToast(form.id ? 'Job updated' : 'Job created');
  };

  const activeCrew = crew.filter(m => m.active !== false).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));

  const handleDragStart = (e, job, dateStr, fromCrewId) => {
    if (job._isSpanDay) { e.preventDefault(); return; }
    setDragJob({ jobId: job.id, fromDate: dateStr, fromCrewId });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', job.id);
  };

  const handlePanelDragStart = (e, job) => {
    setDragJob({ jobId: job.id, fromDate: job.move_date, fromPanel: true });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', job.id);
  };

  const handleDragOver = (e, key) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOver(key);
    // Auto-expand: when dragging near the right edge of the board and we're
    // at 1-week zoom, expand to 2 weeks so the user can drop across the week
    // boundary without manually zooming.
    if (zoomWeeks === 1 && boardRef.current) {
      const rect = boardRef.current.getBoundingClientRect();
      const pxFromRight = rect.right - e.clientX;
      if (pxFromRight < 80) {
        setZoomWeeks(2);
        autoExpandedRef.current = true;
      }
    }
  };

  const handleDragLeave = () => {
    setDragOver(null);
  };

  // Snap back to 1-week zoom after a drop if the expansion was automatic.
  const snapBackZoom = useCallback(() => {
    if (autoExpandedRef.current) {
      setZoomWeeks(1);
      autoExpandedRef.current = false;
    }
  }, []);

  // Travel phase kinds — these form an out-of-state trip group. When ANY
  // sibling travel pill in the same estimate already has a crew assigned,
  // every other travel pill in that group inherits the same crew/trucks
  // on first drop (Option B cascade). Pack/move are NOT in this list:
  // packing happens before the trip and is free to be assigned to a
  // different crew than the travelers. HOLD is out too (pick C, 2026-09-17):
  // a hold day is a ghost — truck only, never crew (ghostHoldFields).
  const TRAVEL_KINDS = new Set(['load', 'drive', 'unload', 'return']);

  // Look for a sibling travel pill in the same estimate that's already been
  // assigned. Returns { crew_ids, leader_id, truck_ids } on hit, or null.
  // Used by executeDrop to propagate crew/truck assignments across the
  // load/drive/unload/return chunks of a single trip.
  const findAssignedTravelSibling = (job) => {
    if (!job?.estimate_id || !job?.phase_kind || !TRAVEL_KINDS.has(job.phase_kind)) return null;
    const siblings = jobs.filter(j =>
      j.id !== job.id &&
      j.estimate_id === job.estimate_id &&
      j.phase_kind && TRAVEL_KINDS.has(j.phase_kind) &&
      (j.crew_ids || []).length > 0
    );
    if (siblings.length === 0) return null;
    // Prefer the LOAD phase (the "first" travel pill the user mentioned) —
    // fall back to the lowest phase_sequence travel sibling if LOAD isn't
    // assigned yet.
    const load = siblings.find(s => s.phase_kind === 'load');
    const chosen = load || siblings.slice().sort((a, b) => (a.phase_sequence || 0) - (b.phase_sequence || 0))[0];
    return {
      crew_ids: chosen.crew_ids || [],
      leader_id: chosen.leader_id || null,
      truck_ids: chosen.truck_ids || [],
      _sourcePhase: chosen.phase_kind,
    };
  };

  // ── Same-day crew reuse (Jaylen 2026-08-25) ─────────────────────────
  // A 2-person crew often runs 3+ small jobs (deliveries / labor only /
  // dump runs) back to back with the same helper + truck. Dropping the
  // NEXT job on that lead's row used to assign the lead alone — then the
  // dispatcher re-built the same crew/truck in the Assign modal for every
  // job. Now, when the drop-target lead already has an assigned job that
  // day, we offer to copy its whole crew + truck onto the new job.
  // "Just the Lead" keeps the old behavior (for the "swapped the 26' for
  // a 16' at lunch" days — assign fresh via the pill).
  // Returns {crew_ids, leader_id, truck_ids} to apply, or null.
  const offerSameDayAssignment = async (job, dateStr, crewId) => {
    const sources = jobs.filter(j =>
      j.id !== job.id &&
      j.move_date === dateStr &&
      j.leader_id === crewId &&
      j.type !== 'WALKTHROUGH' &&
      j.status !== 'completed' &&
      ((j.crew_ids || []).length > 1 || (j.truck_ids || []).length > 0)
    );
    if (sources.length === 0) return null;
    // Prefer the lead's LATEST job that day — as the day progresses the
    // crew sheds people or swaps trucks, and the most recent setup is the
    // likeliest one to carry forward.
    sources.sort((a, b) => String(a.time || '').localeCompare(String(b.time || '')));
    const src = sources[sources.length - 1];
    const crewNames = (src.crew_ids || []).map(id => getCrew(id)?.name).filter(Boolean).join(' + ');
    const truckNames = (src.truck_ids || []).map(id => trucks.find(t => t.id === id)?.name).filter(Boolean).join(' + ');
    const leadName = getCrew(crewId)?.name || 'This lead';
    const srcClient = getClient(src.client_id)?.name || 'another job';
    const targetClient = getClient(job.client_id)?.name || 'this job';
    const keep = await confirmDialog({
      title: `Use ${leadName}'s crew from ${srcClient} on ${targetClient}?`,
      body: `${srcClient}${src.time ? ` (${formatTime12(src.time)})` : ''} already has ${crewNames || leadName}${truckNames ? ` · ${truckNames}` : ''}. Copy the same crew${truckNames ? ' and truck' : ''} onto ${targetClient}, or assign just ${leadName} and build the crew later.`,
      confirmLabel: `Same crew${truckNames ? ' & truck' : ''}`,
      cancelLabel: 'Just the lead',
    });
    if (!keep) return null;
    return {
      crew_ids: [...(src.crew_ids || [])],
      leader_id: src.leader_id || crewId,
      truck_ids: [...(src.truck_ids || [])],
      _crewNames: crewNames,
      _truckNames: truckNames,
    };
  };

  const executeDrop = async (job, newDate, crewId) => {
    const updates = { move_date: newDate };
    // Cascade rule: if this is a travel phase of a multi-phase trip and a
    // sibling travel pill already has a crew, inherit from the sibling
    // instead of the dispatcher-dropped crewId. This keeps the same people
    // on every leg of the trip automatically.
    const sibling = findAssignedTravelSibling(job);
    let copiedSameDay = null;
    if (kindIsHoldOnly(job.phase_kind)) {
      // Ghost hold: date it, nothing else — no crew prompt, no inheritance.
      updates.status = ['completed', 'cancelled'].includes(job.status) ? job.status : 'assigned';
    } else if (sibling) {
      updates.crew_ids = sibling.crew_ids;
      updates.leader_id = sibling.leader_id;
      updates.truck_ids = sibling.truck_ids;
      updates.status = 'assigned';
    } else if (!job.leader_id || (job.crew_ids || []).length === 0) {
      // Same-day crew reuse: the lead already runs a crew today → offer
      // to bring the whole crew + truck along (see helper above).
      copiedSameDay = await offerSameDayAssignment(job, newDate, crewId);
      if (copiedSameDay) {
        updates.crew_ids = copiedSameDay.crew_ids;
        updates.leader_id = copiedSameDay.leader_id;
        updates.truck_ids = copiedSameDay.truck_ids;
      } else {
        updates.crew_ids = [crewId];
        updates.leader_id = crewId;
      }
      updates.status = 'assigned';
    }
    // Shift trip_tags only when the source has a date — dateless phase
    // chunks (pack/load/unload waiting in the unscheduled rail) can't be
    // "shifted" because they had no origin day to begin with.
    if ((job.duration_days || 1) > 1 && job.trip_tags && job.move_date) {
      const dayDiff = Math.round((parseISO(newDate) - parseISO(job.move_date)) / 86400000);
      const newTags = {};
      Object.entries(job.trip_tags).forEach(([d, tag]) => {
        const shifted = format(addDays(parseISO(d), dayDiff), 'yyyy-MM-dd');
        newTags[shifted] = tag;
      });
      updates.trip_tags = newTags;
    }
    const updated = await saveJob({ ...job, ...updates });

    // ── Anchor auto-cascade ────────────────────────────────────────────
    // When a LOAD or MOVE pill (the "anchor") is placed on the board,
    // automatically schedule all dateless siblings in the same estimate.
    // Phases before the anchor (PACK) go to preceding days, phases after
    // (DRIVE, UNLOAD, RETURN, etc.) go to subsequent days, each respecting
    // their own duration_days.
    // Anchor = any kind containing a load/move segment (phaseKinds.js).
    if (kindIsAnchor(job.phase_kind) && job.phase_kind && job.estimate_id) {
      const addDaysISO = (iso, d) => format(addDays(parseISO(iso), d), 'yyyy-MM-dd');
      const siblings = jobs.filter(j =>
        j.id !== job.id &&
        j.estimate_id === job.estimate_id &&
        !j.move_date
      );
      if (siblings.length > 0) {
        siblings.sort((a, b) => (a.phase_sequence || 0) - (b.phase_sequence || 0));
        const anchorSeq = job.phase_sequence || 0;
        const before = siblings.filter(s => (s.phase_sequence || 0) < anchorSeq);
        const after  = siblings.filter(s => (s.phase_sequence || 0) > anchorSeq);

        // Truck cascades: the anchor's truck stays with the load through
        // the entire trip chain until RETURN completes. PACK phases before
        // the anchor do NOT get the truck (packers don't need the truck).
        const anchorTrucks = updates.truck_ids || job.truck_ids || [];

        // Place phases BEFORE the anchor (e.g. PACK) working backwards
        let daysBefore = 0;
        for (let i = before.length - 1; i >= 0; i--) {
          daysBefore += Math.max(1, before[i].duration_days || 1);
          const sibDate = addDaysISO(newDate, -daysBefore);
          const sibCrew = updates.crew_ids || job.crew_ids || [];
          await saveJob({
            ...before[i],
            move_date: sibDate,
            crew_ids: sibCrew,
            leader_id: sibCrew[0] || null,
            truck_ids: kindInTruckChain(before[i].phase_kind) ? anchorTrucks : (before[i].truck_ids || []),
            // Sibling reshuffles keep live statuses (QA M5 BUG-3).
            status: ['dispatched', 'in_progress'].includes(before[i].status) ? before[i].status : 'assigned',
          });
        }

        // Place phases AFTER the anchor (DRIVE, UNLOAD, RETURN…)
        let daysAfter = Math.max(1, job.duration_days || 1);
        for (const sib of after) {
          const sibDate = addDaysISO(newDate, daysAfter);
          // Hold days ride along with the truck only (ghostHoldFields
          // enforces it on save; this keeps the intent readable here).
          const sibCrew = kindIsHoldOnly(sib.phase_kind) ? [] : (updates.crew_ids || job.crew_ids || []);
          await saveJob({
            ...sib,
            move_date: sibDate,
            crew_ids: sibCrew,
            leader_id: sibCrew[0] || null,
            truck_ids: kindInTruckChain(sib.phase_kind) ? anchorTrucks : (sib.truck_ids || []),
            // Sibling reshuffles keep live statuses (QA M5 BUG-3).
            status: ['dispatched', 'in_progress'].includes(sib.status) ? sib.status : 'assigned',
          });
          daysAfter += Math.max(1, sib.duration_days || 1);
        }
      }
    }
    // ── End anchor auto-cascade ────────────────────────────────────────

    if (kindIsHoldOnly(job.phase_kind)) {
      // db.upsertJob stripped the crew — say so, or "Assigned to Marcus"
      // would be a lie the dispatcher then goes looking for on his row.
      const days = Math.max(1, job.duration_days || 1);
      showToast(`Hold placed on ${format(parseISO(newDate), 'EEE MMM d')}${days > 1 ? ` (${days} days)` : ''} — truck only, no crew`, 'success', { description: 'Holds show as a grey chip under the lead who loaded the truck.' });
    } else if (sibling) {
      showToast(`Inherited crew from ${String(sibling._sourcePhase).toUpperCase()} — landed on ${format(parseISO(newDate), 'EEE MMM d')}`);
    } else if (copiedSameDay) {
      showToast(`Assigned with same crew${copiedSameDay._truckNames ? ' & truck' : ''} — ${copiedSameDay._crewNames || getCrew(crewId)?.name}${copiedSameDay._truckNames ? ` · ${copiedSameDay._truckNames}` : ''}`);
    } else {
      showToast(`${crewId && !job.leader_id ? 'Assigned to ' + (getCrew(crewId)?.name || 'crew') + ' on ' : 'Moved to '}${format(parseISO(newDate), 'EEE MMM d')}`);
    }
  };

  const handleDrop = async (e, dateStr, crewId) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragJob) return;

    // ── OFF-day guard (Susie 2026-08-06) ──
    // Dropping a job on someone who's off (right-click OFF, a specific
    // day-off request, or their recurring weekly day) used to sail
    // through silently. Now it takes an explicit confirm.
    {
      const offInfo = crewOffInfo(getCrew(crewId), dateStr, availability);
      if (offInfo.off) {
        const who = getCrew(crewId)?.name || 'This crew member';
        const dropClient = dragJob.isNewClient ? dragJob.clientData?._clientName
          : dragJob.isNewEstimate ? dragJob.estimateData?._clientName
          : getClient(jobs.find(j => j.id === dragJob.jobId)?.client_id)?.name;
        const ok = await confirmDialog({
          title: `Assign ${who} to ${dropClient || 'this job'} on their day off?`,
          body: `${who} is off ${format(parseISO(dateStr), 'EEE, MMM d')} — ${offInfo.source === 'recurring'
            ? 'their weekly day off'
            : `${offInfo.status === 'scheduled_off' ? 'scheduled off' : 'requested off'}${offInfo.note ? ` ("${offInfo.note}")` : ''}`}. The day off stays on the board; the job is assigned on top of it.`,
          confirmLabel: 'Assign anyway',
        });
        if (!ok) { setDragJob(null); return; }
      }
    }

    if (dragJob.isNewEstimate) {
      const ed = dragJob.estimateData;
      const dateChanged = ed.move_date !== dateStr;
      const createEstimateJob = async (targetDate) => {
        const newJob = await saveJob({
          client_id: ed.client_id,
          estimate_id: ed._estimateId,
          type: ed.type,
          move_date: targetDate,
          time: '08:00',
          from_address: ed.from_address || '',
          to_address: ed.to_address || '',
          heavy_items: ed.heavy_items || '',
          packing: ed.packing || '',
          crew_ids: [crewId],
          leader_id: crewId,
          status: 'assigned',
        });
        const est = estimates.find(e => e.id === ed._estimateId);
        if (est) await saveEstimate({ ...est, status: 'dispatched' });
        showToast(`Scheduled ${ed._jobNumber} — assigned to ${getCrew(crewId)?.name || 'crew'}`);
      };
      if (dateChanged) {
        setDateConfirm({
          job: null,
          isNewEstimate: true,
          estimateData: ed,
          newDate: dateStr,
          newCrewId: crewId,
          oldDate: ed.move_date,
        });
      } else {
        await createEstimateJob(dateStr);
      }
      setDragJob(null);
      return;
    }

    // Whole-trip drag: the dispatcher grabbed an entire client group card
    // from the right-side pool. Place every leg in one shot, preserving the
    // original relative offsets between legs.
    //   - If the legs already have move_dates (the common case — the planner
    //     seeded them), the earliest leg lands on the drop day and the rest
    //     shift by the same delta.
    //   - If all legs are dateless, each leg gets one consecutive day
    //     starting from the drop day (phase_sequence order).
    // Crew assignment: the dispatcher drops on a crew row, so that crew goes
    // on every leg. The subsequent drive-crew cascade is a no-op since every
    // leg already got the same crew in this pass.
    if (dragJob.isTripGroup) {
      const tripJobs = jobs.filter(j => dragJob.jobIds.includes(j.id));
      if (tripJobs.length === 0) { setDragJob(null); return; }
      tripJobs.sort((a, b) =>
        (a.phase_sequence || 0) - (b.phase_sequence || 0) ||
        (a.move_date || '\uffff').localeCompare(b.move_date || '\uffff')
      );
      const addDaysISO = (iso, days) => format(addDays(parseISO(iso), days), 'yyyy-MM-dd');
      const datedJobs = tripJobs.filter(j => j.move_date);
      const allDated = datedJobs.length === tripJobs.length;

      let placements;
      if (allDated) {
        // All jobs have dates — delta-shift them to the drop date
        const anchor = datedJobs[0].move_date;
        const deltaDays = Math.round((parseISO(dateStr) - parseISO(anchor)) / 86400000);
        placements = tripJobs.map(j => ({
          job: j,
          newDate: addDaysISO(j.move_date, deltaDays),
        }));
      } else {
        // Mixed or all dateless — lay out sequentially by phase_sequence,
        // anchored on the drop date. Each phase gets consecutive days
        // respecting duration_days. This handles the common case of
        // Unassign All → re-drag where only LOAD has a date.
        let offset = 0;
        placements = tripJobs.map(j => {
          const newDate = addDaysISO(dateStr, offset);
          offset += Math.max(1, j.duration_days || 1);
          return { job: j, newDate };
        });
      }

      // Cascade truck from anchor: the LOAD/MOVE truck stays with the
      // trip through RETURN. PACK phases are excluded.
      const anchorJob = tripJobs.find(j => j.phase_kind === 'load' || j.phase_kind === 'move');
      const anchorTrucks = anchorJob?.truck_ids || [];

      for (const { job, newDate } of placements) {
        await saveJob({
          ...job,
          move_date: newDate,
          crew_ids: [crewId],
          leader_id: crewId,
          truck_ids: kindInTruckChain(job.phase_kind) && anchorTrucks.length > 0 ? anchorTrucks : (job.truck_ids || []),
          status: 'assigned',
        });
      }
      showToast(`Placed ${tripJobs.length}-leg trip starting ${format(parseISO(dateStr), 'EEE MMM d')} — assigned to ${getCrew(crewId)?.name || 'crew'}`);
      setDragJob(null);
      return;
    }

    if (dragJob.isNewClient) {
      const cd = dragJob.clientData;
      const dateChanged = cd.move_date !== dateStr;
      if (dateChanged) {
        setDateConfirm({
          job: null,
          isNewClient: true,
          clientData: cd,
          newDate: dateStr,
          newCrewId: crewId,
          oldDate: cd.move_date,
        });
      } else {
        await saveJob({
          client_id: cd._clientId,
          type: cd.type,
          move_date: dateStr,
          time: cd.time || '08:00',
          from_address: cd.from_address || '',
          to_address: cd.to_address || '',
          crew_ids: [crewId],
          leader_id: crewId,
          status: 'assigned',
        });
        showToast(`Created job for ${cd._clientName} — assigned to ${getCrew(crewId)?.name || 'crew'}`);
      }
      setDragJob(null);
      return;
    }

    const job = jobs.find(j => j.id === dragJob.jobId);
    if (!job) { setDragJob(null); return; }

    const isFromPanel = dragJob.fromPanel;
    const dateChanged = job.move_date !== dateStr;
    const sameDate = !dateChanged;
    // Dateless phase chunks (pack/load/unload waiting in the unscheduled
    // pool) have move_date = null. Scheduling one for the first time is
    // not really a "date change" — there's no old date to confirm away
    // from — so skip the confirmation dialog and just land it.
    const isInitialSchedule = !job.move_date;

    // Vertical reassign: pill dragged from one crew row to another on the
    // same date. Skip the full executeDrop path (which has anchor cascade
    // logic) and just swap the crew directly.
    const fromCrewRow = dragJob.fromCrewId;
    const crewChanged = fromCrewRow ? (crewId !== fromCrewRow) : !(job.crew_ids || []).includes(crewId);
    const isVerticalReassign = !isFromPanel && sameDate && !isInitialSchedule && crewChanged;

    if (isVerticalReassign) {
      // Determine WHO was dragged based on fromCrewId.
      // If the pill was dragged from the lead's row → swap lead.
      // If dragged from a non-lead member's row → swap just that member.
      const fromCrewId = dragJob.fromCrewId;
      const isDraggingLead = !fromCrewId || fromCrewId === job.leader_id;
      let newCrewIds = [...(job.crew_ids || [])];
      let newLeader = job.leader_id;

      if (isDraggingLead) {
        // Replace old leader with the drop target, keep everyone else
        newCrewIds = newCrewIds.filter(id => id !== crewId);
        const leaderIdx = newCrewIds.indexOf(job.leader_id);
        if (leaderIdx >= 0) {
          newCrewIds[leaderIdx] = crewId;
        } else {
          newCrewIds.unshift(crewId);
        }
        newLeader = crewId;
        // Ensure leader is first
        newCrewIds = [crewId, ...newCrewIds.filter(id => id !== crewId)];
      } else {
        // Swap just the non-lead member for the drop target
        const memberIdx = newCrewIds.indexOf(fromCrewId);
        if (memberIdx >= 0) {
          newCrewIds[memberIdx] = crewId;
        } else {
          newCrewIds.push(crewId);
        }
        // Dedupe in case crewId was already in the list
        newCrewIds = [...new Set(newCrewIds)];
      }

      const updated = await saveJob({
        ...job,
        crew_ids: newCrewIds,
        leader_id: newLeader,
        // Reassigning crew never downgrades a live job (QA M5 BUG-3).
        status: ['dispatched', 'in_progress'].includes(job.status) ? job.status : 'assigned',
      });
      // Cascade: if a DRIVE/DRIVE_UNLOAD pill is reassigned, push the new
      // crew to UNLOAD/RETURN siblings so the whole trip stays in sync.
      if (isDraggingLead) {
        await cascadeDriveCrewToSiblings({ ...job, ...updated, crew_ids: newCrewIds, leader_id: newLeader });
      }
      const movedName = getCrew(fromCrewId)?.name || 'crew member';
      const targetName = getCrew(crewId)?.name || 'crew';
      showToast(isDraggingLead ? `Reassigned lead to ${targetName}` : `Swapped ${movedName} → ${targetName}`);
    } else if (isFromPanel && sameDate) {
      await executeDrop(job, dateStr, crewId);
    } else if (isInitialSchedule) {
      await executeDrop(job, dateStr, crewId);
    } else if (isFromPanel && dateChanged) {
      setDateConfirm({ job, newDate: dateStr, newCrewId: crewId, oldDate: job.move_date });
    } else if (dateChanged) {
      setDateConfirm({ job, newDate: dateStr, newCrewId: crewId, oldDate: dragJob.fromDate });
    }
    setDragJob(null);
  };

  const handleUnassignDrop = async (e) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragJob) return;
    // Only allow unassigning real jobs (not estimate drafts or new client entries)
    if (dragJob.isNewEstimate || dragJob.isNewClient) { setDragJob(null); return; }
    const job = jobs.find(j => j.id === dragJob.jobId);
    if (!job) { setDragJob(null); return; }
    // If already unassigned, just bail
    if ((job.crew_ids || []).length === 0 && !job.leader_id) { setDragJob(null); return; }
    const clientName = getClient(job.client_id)?.name || 'this job';
    const proceed = await confirmDialog({
      title: `Unassign ${clientName}?`,
      body: `Crew and truck come off ${clientName}'s job and it moves back to the Unassigned pool. The job, its date and notes are kept.`,
      confirmLabel: 'Unassign',
    });
    if (!proceed) { setDragJob(null); return; }
    try {
      const previous = { ...job };
      let revertedEstimate = null;
      await saveJob({ ...job, crew_ids: [], leader_id: null, truck_ids: [], status: 'unassigned' });
      // Revert linked estimate status back to 'booked' so it stays schedulable.
      // Note: legacy check for 'dispatched' status — in the three-status system
      // estimates no longer transition to 'dispatched' so this branch is a
      // defensive no-op on modern data, kept to handle any leftover old rows.
      if (job.estimate_id) {
        const est = estimates.find(x => x.id === job.estimate_id);
        if (est && est.status === 'dispatched') {
          revertedEstimate = est;
          await saveEstimate({ ...est, status: 'booked' });
        }
      }
      // 1B: Undo puts the prior crew / truck / status back on the job.
      showToast(`Unassigned ${clientName}`, undefined, {
        undo: async () => {
          try {
            await saveJob(previous);
            if (revertedEstimate) await saveEstimate(revertedEstimate);
            showToast(`Re-assigned ${clientName}`);
          } catch (err) { showToast('Error: ' + err.message, 'error'); }
        },
      });
    } catch (err) {
      showToast('Error: ' + err.message, 'error');
    }
    setDragJob(null);
  };

  const confirmDateChange = async () => {
    if (!dateConfirm) return;
    if (dateConfirm.isNewEstimate) {
      const ed = dateConfirm.estimateData;
      await saveJob({
        client_id: ed.client_id,
        estimate_id: ed._estimateId,
        type: ed.type,
        move_date: dateConfirm.newDate,
        time: '08:00',
        from_address: ed.from_address || '',
        to_address: ed.to_address || '',
        heavy_items: ed.heavy_items || '',
        packing: ed.packing || '',
        crew_ids: [dateConfirm.newCrewId],
        leader_id: dateConfirm.newCrewId,
        status: 'assigned',
      });
      const est = estimates.find(e => e.id === ed._estimateId);
      if (est) await saveEstimate({ ...est, status: 'dispatched' });
      showToast(`Scheduled ${ed._jobNumber} on ${format(parseISO(dateConfirm.newDate), 'EEE MMM d')}`);
    } else if (dateConfirm.isNewClient) {
      const cd = dateConfirm.clientData;
      await saveJob({
        client_id: cd._clientId,
        type: cd.type,
        move_date: dateConfirm.newDate,
        time: cd.time || '08:00',
        from_address: cd.from_address || '',
        to_address: cd.to_address || '',
        crew_ids: [dateConfirm.newCrewId],
        leader_id: dateConfirm.newCrewId,
        status: 'assigned',
      });
      showToast(`Created job for ${cd._clientName} on ${format(parseISO(dateConfirm.newDate), 'EEE MMM d')}`);
    } else {
      await executeDrop(dateConfirm.job, dateConfirm.newDate, dateConfirm.newCrewId);
      // Moves rule 3 (2026-09-18): the dialog's "Date is: Confirmed /
      // Tentative" keys. The drop itself already wrote the move's date
      // (store.saveJob mirrors the leg's date onto the move); a Tentative
      // pick flips the flag of the date this leg rides on — pack for a
      // pack-only leg, unload for unload-side legs, else load. Confirmed
      // is the default and needs no write (the store leaves the flag as
      // it was — a date drag is not a confirmation, rule 5).
      if (dateConfirm.tentative && dateConfirm.job?.move_id && typeof saveMove === 'function') {
        const key = jobDateKey(dateConfirm.job, movesById.get(dateConfirm.job.move_id));
        try { await saveMove({ id: dateConfirm.job.move_id, [`${key}_tentative`]: true }); }
        catch (err) { showToast('Date moved, but marking it tentative failed: ' + err.message, 'error'); }
      }
    }
    setDateConfirm(null);
  };

  // Legacy trip-planner handlers (enterSpanMode, confirmTripCrew, handleSpanClick,
  // cycleSpanTag, handleSpanSave, handleSpanCancel) were removed in the Phase
  // Planner merge. Multi-day trips are now built as separate phase rows on the
  // estimate (pack/load/drive/unload/return/hold) and each becomes its own
  // move_jobs pill dispatched onto a day like any other chunk.

  const handleSaveAssignment = async (updated) => {
    await saveJob(updated);
    // Drive-crew cascade: if this was the DRIVE pill of a multi-leg trip,
    // copy the same crew/leader/trucks onto the UNLOAD and RETURN siblings
    // in the same estimate. The dispatcher assigning drive is implicitly
    // assigning "who's on the trip" — load is intentionally excluded since
    // origin-side loading crews are often different from the traveling crew.
    const cascaded = await cascadeDriveCrewToSiblings(updated);
    // Truck cascade: if a truck was assigned to any phase in the trip chain,
    // propagate it to all siblings (load → return). The truck is physically
    // committed for the entire trip.
    const truckCascaded = await cascadeTruckToTripChain(updated);
    const messages = [];
    if (cascaded > 0) messages.push(`drive crew → ${cascaded} leg${cascaded === 1 ? '' : 's'}`);
    if (truckCascaded > 0) messages.push(`truck → ${truckCascaded} leg${truckCascaded === 1 ? '' : 's'}`);
    showToast(messages.length > 0 ? `Job assigned · ${messages.join(', ')}` : 'Job assigned');
  };

  const handleSaveCrewOrder = async (orderedIds) => {
    await reorderCrew(orderedIds);
    showToast('Crew order saved');
  };

  const weekEndStr = format(addDays(weekStart, weekDays.length - 1), 'yyyy-MM-dd');
  const weekStartStr = format(weekStart, 'yyyy-MM-dd');
  // ALL unassigned jobs across every week — used by the right-side sidebar
  // so the trip-group card always shows the full trip (PACK → LOAD → DRIVE →
  // HOLD ×9 → UNLOAD → RETURN) no matter which week the board is viewing.
  //
  // Defensive predicate (changed 2026-05-12 after Jaylen bug): a job is
  // treated as unassigned ONLY when BOTH status='unassigned' AND crew_ids
  // is empty. The earlier OR-predicate would surface a row in the
  // Unassigned strip if EITHER signal said so, which caused inconsistent
  // rows (status='unassigned' + crew_ids populated) to render in TWO
  // places — once here in the Unassigned strip, again in each crew row.
  // The defensive AND keeps the visual sane regardless of how the data
  // drifts. Bonus: cancelled/completed jobs with empty crew_ids no
  // longer accidentally show up in unassigned anymore either.
  // 2026-07-08 (QA M2 NEW-8): broadened from status==='unassigned' only.
  // Once in_progress became saveable (sql/069), an UNCREWED in-progress
  // job vanished from the pool AND Active Moves — unreachable from the
  // whole UI. The pool now takes any uncrewed, non-terminal job. The
  // 2026-05-12 double-render guarantee holds: crew rows only render jobs
  // WITH crew, the pool only jobs WITHOUT — disjoint by construction.
  // Ghost holds (pick C, 2026-09-17): a DATED hold-only row has no crew by
  // design, so it must not fall into the pool — it's placed. Undated holds
  // still ride in the trip card so the whole trip drags as one.
  const POOL_TERMINAL = ['completed', 'cancelled'];
  const allUnassignedJobs = jobs.filter(j => {
    if (j.type === 'WALKTHROUGH') return false;
    if ((j.crew_ids || []).length !== 0) return false;
    if (POOL_TERMINAL.includes(j.status)) return false;
    if (j.move_date && kindIsHoldOnly(j.phase_kind)) return false;
    return true;
  });

  // Ghost holds on the board: every dated hold-only row, spanning its
  // duration_days, drawn as a grey chip in the cell of the lead who LOADED
  // the truck (the load/move sibling's leader — or, failing that, any
  // truck-chain sibling with a lead). Holds whose loader is unknown yet
  // land as a chip on the Unassigned row so they're never invisible.
  const ghostHolds = useMemo(() => {
    const out = [];
    for (const j of jobs) {
      if (!j.move_date || j.status === 'cancelled' || !kindIsHoldOnly(j.phase_kind)) continue;
      const sibs = j.estimate_id ? jobs.filter(s => s.id !== j.id && s.estimate_id === j.estimate_id && s.leader_id) : [];
      const loader = sibs.find(s => kindLoadsTruck(s.phase_kind))
        || sibs.filter(s => kindInTruckChain(s.phase_kind)).sort((a, b) => (a.phase_sequence || 0) - (b.phase_sequence || 0))[0]
        || null;
      const days = Math.max(1, j.duration_days || 1);
      let start;
      try { start = parseISO(j.move_date); } catch { continue; }
      for (let i = 0; i < days; i++) {
        out.push({ job: j, dateStr: format(addDays(start, i), 'yyyy-MM-dd'), dayNum: i + 1, days, loaderId: loader?.leader_id || null, loader });
      }
    }
    return out;
  }, [jobs]);
  const ghostHoldsFor = (crewId, dateStr) => ghostHolds.filter(g => g.dateStr === dateStr && g.loaderId === crewId);

  // The grey chip itself (showroom C): "HOLD · Margie L." pinned to the
  // bottom of the cell. Tap → where the truck is and who to call. Not a
  // drag target, not a job pill — it never reads as work for that lead.
  const renderGhostHold = (g, rowKey) => {
    const { job, dateStr, dayNum, days, loader } = g;
    const client = getClient(job.client_id);
    const first = client?.name ? client.name.split(' ')[0] + (client.name.split(' ')[1] ? ' ' + client.name.split(' ')[1][0] + '.' : '') : '?';
    const key = 'ghost:' + job.id + rowKey + dateStr;
    const open = expandedPill === key;
    const truckNames = (job.truck_ids || []).map(id => getTruck(id)?.name).filter(Boolean);
    const loaderCrew = loader ? getCrew(loader.leader_id) : null;
    return (
      <div key={key} className="mt-auto pt-1 relative" onClick={e => e.stopPropagation()}>
        <button type="button"
          className={`inline-flex items-center gap-1 max-w-full ${zoomWeeks >= 3 ? 'px-1 text-[8.5px]' : 'px-1.5 text-[10px]'} py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-600 hover:bg-slate-200 leading-tight`}
          title={`${client?.name || 'Hold'} — loaded truck parked${truckNames.length ? ' · ' + truckNames.join(' + ') : ''}${days > 1 ? ` · day ${dayNum} of ${days}` : ''}`}
          onClick={() => setExpandedPill(open ? null : key)}>
          <span className="font-black tracking-wider">HOLD</span>
          <span className="truncate">· {first}</span>
          {days > 1 && zoomWeeks < 3 && <span className="text-slate-400 font-semibold">{dayNum}/{days}</span>}
        </button>
        {open && (
          <div className="absolute left-0 top-full z-50 bg-white border border-slate-300 rounded-lg shadow-2xl p-4 w-72 mt-1 text-slate-900" style={{ zIndex: 999 }}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-bold text-sm">{client?.name || 'Hold'} — Hold{days > 1 ? ` · day ${dayNum} of ${days}` : ''}</div>
              <IconButton label="Close" size="sm" className="-my-1 -mr-1" onClick={() => setExpandedPill(null)}><IconX size={14}/></IconButton>
            </div>
            <div className="text-xs text-slate-500 mb-3">Loaded truck, parked. Commits the truck only — nobody is on the clock.</div>
            <div className="text-xs space-y-1.5 mb-3">
              <div><strong>Truck:</strong> {truckNames.length ? truckNames.join(' + ') : <span className="text-amber-700 font-semibold">none assigned</span>}</div>
              <div><strong>Loaded by:</strong> {loaderCrew ? <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: loaderCrew.display_color }}></span>{loaderCrew.name}</span> : <span className="text-slate-400">not placed yet</span>}</div>
              {job.from_address && <div><strong>From:</strong> {job.from_address}</div>}
              {job.to_address && <div><strong>To:</strong> {job.to_address}</div>}
              {client?.phone && <div><strong>Client:</strong> <a className="text-blue-700 hover:underline" href={`tel:${client.phone}`}>{client.phone}</a></div>}
            </div>
            <div className="flex gap-2">
              <Button variant="primary" size="sm" onClick={() => { setExpandedPill(null); setAssignModal({ ...job, _assignMode: 'truck' }); }}>Truck assignment</Button>
              <Button variant="secondary" size="sm" onClick={() => { setExpandedPill(null); setJobModal(job); }}>Open job</Button>
            </div>
          </div>
        )}
      </div>
    );
  };
  const allUnassigned = allUnassignedJobs.map(j => ({ ...j, _source: 'job' }));

  // Week-scoped subset for the on-board "Unassigned" row — only dated jobs
  // inside the visible week show up on the grid.
  const unassigned = allUnassigned.filter(item =>
    !item.move_date || (item.move_date >= weekStartStr && item.move_date <= weekEndStr)
  );

  // Canonical phase-kind ordering for the per-kind unassigned row headers
  // and for sorting legs within a trip group. Mirrors the chronology of a
  // typical out-of-state move (pack → load → drive → unload → hold → return).
  // (Kind chip labels/order now generated by phaseKinds.js.)
  // Service-type jobs (delivery / pickup / labor / packing) aren't "moves" —
  // show their type as the Unassigned-pool chip instead of the generic MOVE
  // phase badge. LOCAL / LONG_DISTANCE keep their phase chips.
  const SERVICE_TYPE_CHIP = { DELIVERY: 'DELIVERY', PICKUP: 'PICKUP', LABOR_ONLY: 'LABOR', PACKING: 'PACKING', DUMP_RUN: 'DUMP' };

  // Dated unassigned items inside the visible week, rendered as a single
  // "Unassigned" row on the board. Each pill carries its own PHASE_KIND
  // badge (PACK / LOAD / DRIVE / …) so dispatchers can tell what kind of
  // work it is without needing a separate row per kind.
  // Unassigned WALKTHROUGHs (type='WALKTHROUGH', no crew yet) surface here in
  // the on-board Unassigned row so a freshly-scheduled walkthrough is visible
  // on its date and can be dragged onto an estimator. They're deliberately
  // kept OUT of the right-side trip pool (allUnassigned excludes them) since
  // they aren't move-trip legs. (Jaylen 2026-06-26: scheduled walkthroughs
  // weren't appearing on the board at all because they were filtered out of
  // both the crew rows — no crew — and the unassigned lane.)
  // Past unresolved walkthroughs (Jaylen 2026-08-07, round 3 — the
  // inline-button pills made the rail HUGE): past walkthroughs leave the
  // rail entirely. The rail only carries today-or-future walkthroughs;
  // everything past + unanswered collects in `pastWtJobs`, surfaced as
  // ONE compact amber "⚠ N past" chip on the Unassigned row header that
  // opens a resolve list (✓ It Happened / Reschedule). Past ones whose
  // CLIENT already completed in the app need nothing — their job rows
  // get closed by the resolve/cleanup path and they simply drop off.
  const todayStrForWt = format(new Date(), 'yyyy-MM-dd');
  const unassignedWalkthroughsInWeek = jobs
    .filter(j => j.type === 'WALKTHROUGH'
      && (j.crew_ids || []).length === 0
      && j.status !== 'completed' && j.status !== 'cancelled'
      && j.move_date && j.move_date >= weekStartStr && j.move_date <= weekEndStr
      && j.move_date >= todayStrForWt)
    .map(j => ({ ...j, _source: 'job' }));
  const unassignedInWeek = [
    ...unassigned.filter(item =>
      item.move_date && item.move_date >= weekStartStr && item.move_date <= weekEndStr
    ),
    ...unassignedWalkthroughsInWeek,
  ];
  // Every past unanswered walkthrough, any week — the resolve list.
  const pastWtJobs = jobs
    .filter(j => j.type === 'WALKTHROUGH'
      && (j.crew_ids || []).length === 0
      && j.status !== 'completed' && j.status !== 'cancelled'
      && j.move_date && j.move_date < todayStrForWt
      && getClient(j.client_id)?.walkthrough_status !== 'completed')
    .sort((a, b) => a.move_date.localeCompare(b.move_date));

  // Right-side pool grouping: built from allUnassigned (every unassigned leg
  // regardless of week) so the sidebar card always shows the full trip. One
  // card per estimate (= one trip), plus legacy flat cards for loose items.
  const tripGroups = (() => {
    const byEstimate = new Map();
    const loose = [];
    allUnassigned.forEach(item => {
      if (item._source !== 'job') { loose.push(item); return; }
      const key = item.estimate_id || null;
      if (!key) { loose.push(item); return; }
      if (!byEstimate.has(key)) byEstimate.set(key, { estimateId: key, client: getClient(item.client_id), legs: [] });
      byEstimate.get(key).legs.push(item);
    });
    const groups = [];
    byEstimate.forEach(g => {
      g.legs.sort((a, b) => (a.phase_sequence || 0) - (b.phase_sequence || 0) || (a.move_date || '').localeCompare(b.move_date || ''));
      groups.push(g);
    });
    return { groups, loose };
  })();

  // Cascade helper: DRIVE crew assignment propagates to UNLOAD and RETURN
  // siblings in the same estimate. Called from handleSaveAssignment below
  // and exposed here so the drop handler can use the same logic.
  const cascadeDriveCrewToSiblings = async (driveJob) => {
    // Segment-derived (phaseKinds.js, 2026-08-26): a drive-like source is
    // any kind with a drive segment and no load (drive, drive_unload,
    // drive+unload+return, ...); targets are downstream unload/return
    // legs — again excluding self-contained load combos.
    const segsOf = (k) => kindSegments(k || '');
    const srcSegs = segsOf(driveJob.phase_kind);
    const isDriveLike = srcSegs.includes('drive') && !srcSegs.includes('load');
    if (!isDriveLike || !driveJob.estimate_id) return 0;
    if (!Array.isArray(driveJob.crew_ids) || driveJob.crew_ids.length === 0) return 0;
    const targets = jobs.filter(j => {
      if (j.id === driveJob.id || j.estimate_id !== driveJob.estimate_id) return false;
      const segs = segsOf(j.phase_kind);
      return (segs.includes('unload') || segs.includes('return')) && !segs.includes('load');
    });
    for (const target of targets) {
      await saveJob({
        ...target,
        crew_ids: driveJob.crew_ids,
        leader_id: driveJob.leader_id || driveJob.crew_ids[0],
        truck_ids: driveJob.truck_ids || [],
        status: target.move_date ? 'assigned' : target.status,
      });
    }
    return targets.length;
  };

  // Cascade helper: when a truck is assigned to any phase in the trip chain
  // (load → hold → drive → unload → return), propagate it to ALL siblings
  // in that chain. The truck is physically committed from LOAD through RETURN.
  const cascadeTruckToTripChain = async (sourceJob) => {
    if (!sourceJob.estimate_id) return 0;
    // Chain membership = any segment that physically carries the truck
    // (phaseKinds.js — pack-only and trash days excluded).
    if (!kindInTruckChain(sourceJob.phase_kind)) return 0;
    const truckIds = sourceJob.truck_ids || [];
    if (truckIds.length === 0) return 0;
    // ── Transfer-truck legs (sql/081, Jaylen 2026-08-10) ────────────────
    // Virginia Erwin case: the 16' shuttle belongs to the LOAD day only,
    // but the old cascade copied the load leg's full truck list onto the
    // whole trip. When the estimate declares a transfer leg:
    //   • edits on a COVERED leg are leg-local — no outbound cascade at
    //     all (the shuttle stays put);
    //   • inbound cascades MERGE into a covered leg (add the chain trucks,
    //     keep its local shuttle) instead of overwriting.
    // So assign the 26' on the drive → it chains everywhere; then add the
    // 16' on the load leg → it stays there.
    const est = estimates.find(e => e.id === sourceJob.estimate_id);
    if (transferTruckCovers(est, sourceJob.phase_kind)) return 0;
    const targets = jobs.filter(j =>
      j.id !== sourceJob.id &&
      j.estimate_id === sourceJob.estimate_id &&
      kindInTruckChain(j.phase_kind)
    );
    let changed = 0;
    for (const target of targets) {
      const nextIds = transferTruckCovers(est, target.phase_kind)
        ? [...new Set([...(target.truck_ids || []), ...truckIds])]
        : truckIds;
      if (JSON.stringify(target.truck_ids || []) === JSON.stringify(nextIds)) continue;
      await saveJob({ ...target, truck_ids: nextIds });
      changed++;
    }
    return changed;
  };

  // Unassigned rail contents — rendered in the right-hand rail at full
  // width, or inside a toolbar popover when the board is narrower than
  // 1100px (dispatch-width.md, aisle 2 pick A).
  const unassignedRail = (
    <div className="bg-white rounded-lg border border-slate-200 p-4 flex-1 overflow-auto"
      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
      onDrop={handleUnassignDrop}>
      <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
        Unassigned
        {allUnassigned.length > 0 && <span className="bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full">{allUnassigned.length}</span>}
      </h3>
      <p className="text-[10px] text-slate-400 mb-3">Drag the card onto the board to place the whole trip — expand to drag individual legs</p>
      <div className="space-y-2">
        {/* Trip groups — one card per estimate with unassigned legs.
            Clicking toggles expansion; the card header is the whole-trip
            drag handle; expanded legs are individually draggable. */}
        {tripGroups.groups.map(group => {
          const isExpanded = expandedTripIds.has(group.estimateId);
          const firstLeg = group.legs[0];
          const datedLegs = group.legs.filter(l => l.move_date);
          const dateRange = datedLegs.length > 0
            ? (() => {
                const sorted = datedLegs.slice().sort((a, b) => a.move_date.localeCompare(b.move_date));
                const first = sorted[0].move_date;
                const last = sorted[sorted.length - 1].move_date;
                try {
                  const fmt = (d) => format(parseISO(d), 'MMM d');
                  return first === last ? fmt(first) : `${fmt(first)} → ${fmt(last)}`;
                } catch { return `${first} → ${last}`; }
              })()
            : (() => {
                // Fall back to estimate move_date if all legs are dateless
                const est = estimates.find(e => e.id === group.estimateId);
                const md = est?.move_date;
                if (md) {
                  try { return 'Move: ' + format(parseISO(md), 'MMM d'); } catch { return 'Move: ' + md; }
                }
                return 'Dateless';
              })();
          const stateAbbr = getStateAbbr(firstLeg?.to_address);
          const isOOS = stateAbbr && stateAbbr !== 'NC';
          const legCount = group.legs.length;
          const jobIds = group.legs.map(l => l.id);
          return (
            <div key={'trip-' + group.estimateId}
              className="bg-white border-2 border-indigo-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              {/* Header — whole-trip drag handle + expand toggle */}
              <div
                draggable
                onDragStart={(e) => {
                  setDragJob({ fromPanel: true, isTripGroup: true, estimateId: group.estimateId, jobIds });
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', 'trip-' + group.estimateId);
                }}
                onDragEnd={() => { setDragJob(null); setDragOver(null); snapBackZoom(); }}
                onClick={() => {
                  setExpandedTripIds(prev => {
                    const next = new Set(prev);
                    if (next.has(group.estimateId)) next.delete(group.estimateId);
                    else next.add(group.estimateId);
                    return next;
                  });
                }}
                className="p-3 bg-indigo-50 hover:bg-indigo-100 cursor-grab active:cursor-grabbing flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-slate-400 text-xs">{isExpanded ? '▼' : '▶'}</span>
                    <div className="font-bold text-slate-900 text-sm truncate">{group.client?.name || '?'}</div>
                    {isOOS && <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded font-bold flex-shrink-0">{stateAbbr}</span>}
                  </div>
                  <div className="text-[11px] font-semibold text-indigo-700 flex items-center gap-1">
                    <span>{legCount} leg{legCount === 1 ? '' : 's'}</span>
                    <span className="opacity-50">·</span>
                    <span>{dateRange}</span>
                  </div>
                  {/* Summary chip row — service type (delivery / pickup /
                      labor / packing) gets one type chip; otherwise one
                      chip per unique phase kind with count. */}
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {SERVICE_TYPE_CHIP[firstLeg?.type] ? (
                      <span className="text-[9px] font-black px-1.5 py-0.5 rounded tracking-wider bg-slate-700 text-white">
                        {SERVICE_TYPE_CHIP[firstLeg.type]}
                      </span>
                    ) : [...new Set(group.legs.map(l => l.phase_kind || 'move'))]
                      .sort((a, b) => kindSortKey(a) - kindSortKey(b))
                      .map(kind => {
                      const n = group.legs.filter(l => (l.phase_kind || 'move') === kind).length;
                      const badge = kindBadge(kind);
                      return (
                        <span key={kind}
                          className="text-[9px] font-black px-1.5 py-0.5 rounded tracking-wider"
                          style={{ background: badge.bg, color: badge.fg }}>
                          {badge.label}{n > 1 ? ` ×${n}` : ''}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
              {/* Expanded legs — each individually draggable */}
              {isExpanded && (
                <div className="p-2 space-y-1 bg-white border-t border-indigo-100">
                  {group.legs.map(leg => {
                    const badge = kindBadge(leg.phase_kind || 'move');
                    const legDateLabel = leg.move_date
                      ? (() => { try { return format(parseISO(leg.move_date), 'EEE MMM d'); } catch { return leg.move_date; } })()
                      : 'UNSCHEDULED';
                    return (
                      <div key={'leg-' + leg.id}
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          handlePanelDragStart(e, leg);
                          e.dataTransfer.setData('text/plain', leg.id);
                        }}
                        onDragEnd={(e) => { e.stopPropagation(); setDragJob(null); setDragOver(null); snapBackZoom(); }}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-grab active:cursor-grabbing">
                        <span className="text-[9px] font-black px-1.5 py-0.5 rounded tracking-wider"
                          style={{ background: badge.bg, color: badge.fg }}>
                          {badge.label}
                        </span>
                        <span className="text-[11px] font-semibold text-slate-700 flex-1 truncate">
                          {legDateLabel}
                        </span>
                        <span className="flex items-center gap-0.5 text-[10px] text-slate-500">
                          <svg width="9" height="10" viewBox="0 0 8 9" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                            <circle cx="4" cy="2.5" r="1.8" fill="#64748b" />
                            <path d="M0.5 8.5C0.5 6.6 2.1 5.2 4 5.2C5.9 5.2 7.5 6.6 7.5 8.5" stroke="#64748b" strokeWidth="1" fill="none" />
                          </svg>
                          {leg.crew_size || leg.crew_size_needed || 0}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {/* Loose items: new-estimate drafts, new-client drafts, and any
            single-leg unassigned jobs that aren't part of a trip. */}
        {tripGroups.loose.map(item => {
          const isClientEntry = item._source === 'client';
          const isEstimateEntry = item._source === 'estimate';
          const client = isClientEntry ? { name: item._clientName } : isEstimateEntry ? { name: item._clientName } : getClient(item.client_id);
          // Dateless phase chunks (pack/load/unload waiting to be
          // scheduled) show an "UNSCHEDULED" badge in place of the day.
          const dayLabel = (() => {
            if (!item.move_date) return null;
            try { return format(parseISO(item.move_date), 'EEE MMM d'); }
            catch { return item.move_date; }
          })();
          const isUnscheduled = !item.move_date;
          const stateAbbr = getStateAbbr(item.to_address);
          const isOutOfState = stateAbbr && stateAbbr !== 'NC';
          const uniqueKey = isClientEntry ? 'client-' + item._clientId : isEstimateEntry ? 'est-' + item._estimateId : item.id;

          const clientRec = isClientEntry ? clients.find(c => c.id === item._clientId) : clients.find(c => c.id === item.client_id);
          // For real job entries, pull pre-allocation from the linked estimate
          const linkedEst = (!isEstimateEntry && !isClientEntry && item.estimate_id) ? estimates.find(e => e.id === item.estimate_id) : null;
          const estForRequirements = isEstimateEntry ? item : linkedEst;
          const crewNeeded = estForRequirements ? (estForRequirements.crew_size_needed || 0) : (clientRec?.crew_size_needed || 0);
          // Truck needed: estimates store string labels ('26ft'/'16ft'/'2x26ft'/'none'), clients may store JSON array of IDs
          const parseTrucks = (raw) => {
            if (!raw) return [];
            // Try JSON array of IDs first
            try {
              const arr = JSON.parse(raw);
              if (Array.isArray(arr)) return arr.map(id => trucks.find(t => t.id === id)?.name).filter(Boolean);
            } catch { /* fall through */ }
            // String label
            const labelMap = { '16ft': "16' Truck", '26ft': "26' Truck", '2x26ft': "2x 26' Trucks", 'none': null };
            const lbl = labelMap[raw];
            if (lbl === null) return [];
            if (lbl) return [lbl];
            return [String(raw)];
          };
          const trucksNeededNames = estForRequirements ? parseTrucks(estForRequirements.trucks_needed) : parseTrucks(clientRec?.trucks_needed);
          // Extra trucks from estimate
          const extraTrucksList = (estForRequirements && estForRequirements.extra_trucks) ? (() => {
            try { const arr = JSON.parse(estForRequirements.extra_trucks); return Array.isArray(arr) ? arr : []; } catch { return []; }
          })() : [];
          const extraTruckLabels = extraTrucksList.map(t => t === '16ft' ? "+16'" : t === '26ft' ? "+26'" : `+${t}`);
          // PACK legs (Jensen 2026-08-25): a packing job's pill was showing
          // the WHOLE move's requirements (26' + extras + full crew). Show
          // the packing crew + packing-vehicle requirement instead.
          const isPackLeg = !isClientEntry && !isEstimateEntry && item.phase_kind === 'pack';
          const packCrewNeeded = isPackLeg ? (Number(item.crew_size) || Number(estForRequirements?.packing_crew_size) || 0) : 0;
          const hasRequirements = isPackLeg || crewNeeded > 0 || trucksNeededNames.length > 0 || extraTruckLabels.length > 0;

          const requirementsBadges = hasRequirements ? (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {isPackLeg ? (
                <>
                  {packCrewNeeded > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">{packCrewNeeded} packer{packCrewNeeded !== 1 ? 's' : ''}</span>}
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold" title="Any 16' truck or the pack van">📦 Pack vehicle</span>
                </>
              ) : (
                <>
                  {crewNeeded > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-bold">{crewNeeded} crew</span>}
                  {trucksNeededNames.map((name, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">{name}</span>)}
                  {extraTruckLabels.map((name, i) => <span key={'x' + i} className="text-[9px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-bold">{name}</span>)}
                </>
              )}
            </div>
          ) : null;

          if (isEstimateEntry) {
            const friendlyType = (item.type || '').replace(/_/g, ' ').toUpperCase();
            return (
              <div key={uniqueKey}
                className="p-3 bg-slate-100 border border-slate-200 rounded-lg cursor-grab active:cursor-grabbing hover:bg-slate-200 hover:border-slate-300 transition-all hover:shadow-md"
                draggable
                onDragStart={(e) => {
                  setDragJob({ fromPanel: true, fromDate: item.move_date, isNewEstimate: true, estimateData: item });
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', 'new-estimate');
                }}
                onDragEnd={() => { setDragJob(null); setDragOver(null); snapBackZoom(); }}
                onClick={() => {
                  setDefaultDate(item.move_date);
                  setJobModal({
                    client_id: item.client_id,
                    estimate_id: item._estimateId,
                    type: item.type,
                    from_address: item.from_address,
                    to_address: item.to_address,
                    move_date: item.move_date,
                    heavy_items: item.heavy_items,
                    packing: item.packing,
                  });
                }}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-bold text-slate-900 text-sm truncate">{client?.name || '?'}</div>
                  {isOutOfState && <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded font-bold flex-shrink-0">{stateAbbr}</span>}
                </div>
                <div className="text-[10px] font-mono text-slate-500 mb-1">{item._jobNumber}</div>
                <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1">
                  <span>{dayLabel}</span>
                  <span className="opacity-50">·</span>
                  <span>{formatTime12(item.time)}</span>
                  <span className="opacity-50">·</span>
                  <span className="text-slate-600">{friendlyType}</span>
                </div>
                {item.from_address && (
                  <div className="text-[10px] text-slate-500 mt-1 truncate">
                    <span className="opacity-70">From: </span>{item.from_address.split(',')[0]?.trim()}
                  </div>
                )}
                {item.to_address && (
                  <div className="text-[10px] text-slate-500 truncate">
                    <span className="opacity-70">To: </span>{(() => {
                      const parts = item.to_address.split(',').map(s => s.trim()).filter(Boolean);
                      return parts.length >= 2 ? `${parts[parts.length - 2]}${stateAbbr ? ', ' + stateAbbr : ''}` : item.to_address;
                    })()}
                  </div>
                )}
                {requirementsBadges}
              </div>
            );
          }

          if (isClientEntry) {
            return (
              <div key={uniqueKey}
                className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm cursor-grab active:cursor-grabbing hover:bg-amber-100 hover:border-amber-300 transition-all hover:shadow-md"
                draggable
                onDragStart={(e) => {
                  setDragJob({ fromPanel: true, fromDate: item.move_date, isNewClient: true, clientData: item });
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', 'new-client');
                }}
                onDragEnd={() => { setDragJob(null); setDragOver(null); snapBackZoom(); }}
                onClick={() => {
                  // Quick Dispatch (2026-08-06): small-job clients get
                  // the fast path — one form that creates the billing-
                  // anchor estimate + the job together. The bare New
                  // Job modal (below) creates an estimate-less job
                  // that reads $0 on Records forever; keep it only
                  // for real-move types.
                  const fullClient = getClient(item._clientId);
                  if (fullClient && QUICK_DISPATCH_TYPES.includes(fullClient.job_type)) {
                    setQuickModal({ client: fullClient, date: item.move_date });
                    return;
                  }
                  setDefaultDate(item.move_date);
                  setJobModal({
                    client_id: item._clientId,
                    type: item.type,
                    from_address: item.from_address,
                    to_address: item.to_address,
                    move_date: item.move_date,
                  });
                }}>
                <div className="flex items-center justify-between mb-1">
                  <div className="font-semibold text-amber-900">{client?.name?.split(' ')[0] || '?'}</div>
                  <span className="bg-amber-200 text-amber-800 text-[10px] px-1.5 py-0.5 rounded font-bold">NEW</span>
                </div>
                <div className="text-xs text-amber-700 flex items-center gap-1">
                  <span className="font-medium">{dayLabel}</span>
                  {isOutOfState && <><span>&bull;</span><span className="font-bold">{stateAbbr}</span></>}
                </div>
                <div className="text-[10px] text-amber-600 mt-0.5">{(item.type || '').replace(/_/g, ' ')} — Drag or click</div>
                {requirementsBadges}
              </div>
            );
          }

          {
            const fromStreet = (item.from_address || client?.origin_address || '').split(',')[0]?.trim() || '';
            const toCity = (() => {
              const addr = item.to_address || client?.destination_address || '';
              const parts = addr.split(',').map(s => s.trim()).filter(Boolean);
              return parts.length >= 2 ? parts[parts.length - 2] : '';
            })();
            const friendlyType = (item.job_label || item.type || '').replace(/_/g, ' ').toUpperCase();
            return (
              <div key={uniqueKey}
                className="p-3 bg-slate-100 border border-slate-200 rounded-lg cursor-grab active:cursor-grabbing hover:bg-slate-200 hover:border-slate-300 transition-all hover:shadow-md"
                draggable
                onDragStart={(e) => handlePanelDragStart(e, item)}
                onDragEnd={() => { setDragJob(null); setDragOver(null); snapBackZoom(); }}>
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="font-bold text-slate-900 text-sm truncate">{client?.name || '?'}</div>
                  {isOutOfState && <span className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0.5 rounded font-bold flex-shrink-0">{stateAbbr}</span>}
                </div>
                <div className="text-[11px] font-semibold text-slate-700 flex items-center gap-1 flex-wrap">
                  {isUnscheduled ? (
                    <span className="px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 text-[9px] font-bold tracking-wide">UNSCHEDULED</span>
                  ) : (
                    <>
                      <span>{dayLabel}</span>
                      <span className="opacity-50">·</span>
                      <span>{formatTime12(item.time)}</span>
                      <span className="opacity-50">·</span>
                    </>
                  )}
                  {item.phase_kind && item.phase_kind !== 'move' && (
                    <span className="px-1 rounded text-[9px] font-bold bg-indigo-100 text-indigo-700 border border-indigo-200">
                      {String(item.phase_kind).toUpperCase().replace('_REMOVAL', '')}
                    </span>
                  )}
                  <span className="text-slate-600">{friendlyType}</span>
                </div>
                {fromStreet && (
                  <div className="text-[10px] text-slate-500 mt-1 truncate">
                    <span className="opacity-70">From: </span>{fromStreet}
                  </div>
                )}
                {toCity && (
                  <div className="text-[10px] text-slate-500 truncate">
                    <span className="opacity-70">To: </span>{toCity}{stateAbbr ? `, ${stateAbbr}` : ''}
                  </div>
                )}
                {requirementsBadges}
              </div>
            );
          }
        })}
        {tripGroups.groups.length === 0 && tripGroups.loose.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-4">All jobs assigned for this week</p>
        )}
      </div>
    </div>
  );

  return (
    <div ref={boardRefCb} className={`flex flex-col h-full min-w-0 ${isTvLayoutActive ? 'p-2' : 'p-6'} ${isFullscreen ? 'bg-slate-50' : ''}`}>
      {/* min-w-0: flex items default to min-width:auto, which would let the
          1wk grid's max-content width push the whole page sideways instead
          of scrolling inside the board (dispatch-width.md). */}
        {/* In TV layout mode we shrink the header chrome aggressively
            so the board itself gets every available vertical pixel. */}
        <div className={`sticky top-0 z-40 bg-white border-b border-slate-200 ${isTvLayoutActive ? 'pb-1 mb-1' : 'pb-3 mb-3'}`}
             style={isTvLayoutActive
               ? { marginLeft: '-8px', marginRight: '-8px', paddingLeft: '8px', paddingRight: '8px', marginTop: '-8px', paddingTop: '8px' }
               : { marginLeft: '-24px', marginRight: '-24px', paddingLeft: '24px', paddingRight: '24px', marginTop: '-24px', paddingTop: '24px' }
             }>
          <div className="flex flex-wrap items-center justify-between gap-y-2 mb-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className={`font-bold font-lemonmilk text-slate-900 ${isTvLayoutActive ? 'text-base' : 'text-2xl'}`}>Dispatch Board</h1>
                  {/* Hidden in TV layout — every pixel goes to rows. */}
                  {!isTvLayoutActive && (
                    <HelpTip title="How the board works">
                      <p>Each row is a crew member, each column a day. <span className="font-semibold">Drag a pill from the Unassigned rail</span> onto a lead's day to schedule it; drag between days or rows to reschedule (you'll get a confirm — it updates the move's date; the client file, the estimate and every view read it).</p>
                      <p>A <span className="font-semibold">dashed amber edge</span> (and a <span className="font-semibold">~</span> before the time) means the move's date is still tentative — a real date on the board, just not confirmed with the client yet. Confirm it on the Estimates page, in the client file, or from the Dashboard's Dates to confirm card.</p>
                      <p>Pill corner tags are the <span className="font-semibold">phase</span> of a multi-day move: PACK, LOAD, DRIVE, UNLOAD, RETURN and combos. Legs of one estimate travel together — moving the anchor shifts its siblings, and trucks chain across the load→drive→unload run.</p>
                      <p><span className="font-semibold">Statuses:</span> assigned (scheduled, crew NOT yet notified) → dispatched (sent to the crew's portal) → in progress (glowing edge — crew is on the clock) → completed. Booked-but-uncrewed jobs sit in the Unassigned rail.</p>
                      <p><span className="font-semibold">Hold days</span> (a loaded truck parked between LOAD and DRIVE) are ghosts: they commit the truck, never the crew. They show as a small grey <span className="font-semibold">HOLD · client</span> chip under the lead who loaded the truck, don't count against crew capacity, and never reach the crew portal. Tap the chip for the truck and who to call.</p>
                      <p><span className="font-semibold">Sending:</span> 📋 Daily Dispatch (toolbar) texts a full day's lineup to every crew on it — its count badge means staged changes are waiting on a send. A pill's Dispatch button texts just that job's crew (or marks it dispatched without texting). Day headers show "✓ Sent" once a day has gone out.</p>
                      <p><span className="font-semibold">Changes after sending:</span> if you edit a job on an already-sent day, crews are auto-notified within a minute, and the day header turns amber — "↻ N changed". Click it to see exactly what changed on which jobs, then resend the day, text one crew, or dismiss what you've handled another way.</p>
                      <p><span className="font-semibold">Views:</span> the switcher next to this icon flips between the Board, the vertical Day view (one day, hour-by-hour, with crew-lead threads and linked-leg connectors), the Trucks board (the week by vehicle — a lead re-using his truck across his own jobs all day is normal and unflagged; red ⚠ means two different crews share a truck that day), and the printable Run Sheet. Same jobs everywhere; the Board stays the default and the only view in TV mode.</p>
                      <p><span className="font-semibold">Portal Preview:</span> 👁 (toolbar) opens a read-only copy of any crew member's portal for any day — exactly what they see on their phone. Hovering a crew name on the left rail shows a quick 👁 for that person. Previews can't submit anything.</p>
                      <p className="text-xs text-slate-400">Drag-and-drop is mouse-only — the same edits are available keyboard-accessibly via Active Moves → any job → edit. Fullscreen + TV mode (top right) are built for the wall display.</p>
                    </HelpTip>
                  )}
                  {/* Dispatch view switcher (Board · Day · …) — hidden in
                      TV layout; the wall display is always the board. */}
                  {!isTvLayoutActive && viewSwitcher}
                </div>
                {/* Subtitle date range is hidden in TV layout mode — the
                    column headers already convey the date range and we
                    want every pixel of vertical real estate for rows. */}
                {!isTvLayoutActive && (
                  <p className="text-slate-600 text-sm">
                    {zoomWeeks === 1
                      ? `Week of ${format(weekStart, 'MMM d, yyyy')}`
                      : `${format(weekStart, 'MMM d')} – ${format(addDays(weekStart, weekDays.length - 1), 'MMM d, yyyy')}`}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1">
                <IconButton label="Previous week" size="sm" onClick={() => setWeekOffset(w => w - 1)}><IconChevronLeft size={18} /></IconButton>
                <Button variant="secondary" size="sm" onClick={() => setWeekOffset(0)}>Today</Button>
                <IconButton label="Next week" size="sm" onClick={() => setWeekOffset(w => w + 1)}><IconChevronRight size={18} /></IconButton>
              </div>
              {/* Zoom selector — visually split into two halves:
                  1wk/2wk are the office laptop views (rich pill content,
                  fits naturally on a regular monitor). 3wk/4wk are the
                  big-display views, intended for the TV in fullscreen.
                  The TV-mode toggle next to the fullscreen icon (right
                  side) flips into chrome-stripped layout. */}
              <div className="flex items-center bg-slate-100 rounded-lg overflow-hidden border border-slate-200">
                {[1,2].map(n => (
                  <button key={n}
                    onClick={() => { setZoomWeeks(n); autoExpandedRef.current = false; }}
                    className={`px-2.5 py-1.5 text-xs font-bold transition-colors ${
                      zoomWeeks === n
                        ? 'bg-red-600 text-white'
                        : 'text-slate-600 hover:bg-slate-200'
                    }`}
                    title={`${n} week${n > 1 ? 's' : ''} — office view`}
                  >{n}wk</button>
                ))}
                {/* Divider — separates office (1/2wk) from TV (3/4wk) */}
                <div className="w-px self-stretch bg-slate-300" aria-hidden />
                {[3,4].map(n => (
                  <button key={n}
                    onClick={() => { setZoomWeeks(n); autoExpandedRef.current = false; }}
                    className={`px-2.5 py-1.5 text-xs font-bold transition-colors ${
                      zoomWeeks === n
                        ? 'bg-red-600 text-white'
                        : 'text-slate-600 hover:bg-slate-200'
                    }`}
                    title={`${n} weeks — TV / big display view`}
                  >{n}wk</button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {undoStack.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => { const msg = await undo(); if (msg) showToast(msg); }}
                  title={`Undo last change (${undoStack.length} available)`}
                >
                  ↩ Undo ({undoStack.length})
                </Button>
              )}
              {redoStack.length > 0 && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={async () => { const msg = await redo(); if (msg) showToast(msg); }}
                  title={`Redo (${redoStack.length} available)`}
                >
                  ↪ Redo ({redoStack.length})
                </Button>
              )}
              {/* Office-only action buttons. Hidden in TV layout mode
                  (2wk/3wk/4wk on a big display) so the board itself gets
                  every vertical pixel — the dispatcher uses 1wk on the
                  laptop for these workflows. */}
              {!isTvLayoutActive && (
                <>
                  <button onClick={() => setHistoryModal(true)} className="bg-slate-500 hover:bg-slate-600 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap" title="View board history & restore">
                    <IconRefresh size={12}/> History
                  </button>
                  <button onClick={() => setQuickText({ crewId: '', message: '' })} className="bg-slate-600 hover:bg-slate-700 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap">💬 Quick Text</button>
                  {/* The one day-send entry point (2026-07-29 button
                      reconciliation — the per-column send buttons are
                      gone). Badge = push notifications held back for
                      today + tomorrow's un-released days: a nudge that
                      staged changes are waiting on a send. */}
                  {(() => {
                    const heldCount = (pendingByDate[format(new Date(), 'yyyy-MM-dd')] || 0)
                      + (pendingByDate[format(addDays(new Date(), 1), 'yyyy-MM-dd')] || 0);
                    return (
                      <button
                        onClick={() => setDailyModal(format(addDays(new Date(), 1), 'yyyy-MM-dd'))}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap"
                        title={heldCount > 0 ? `${heldCount} crew notification${heldCount === 1 ? '' : 's'} held back until you send the day's dispatch` : 'Text a day\'s full lineup to its crews'}
                      >
                        📋 Daily Dispatch
                        {heldCount > 0 && <span className="bg-white/25 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none">{heldCount}</span>}
                      </button>
                    );
                  })()}
                  <button onClick={() => setWeeklyModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap">📱 Weekly Schedule</button>
                  {/* Portal Preview (2026-08-06, Jaylen) — see EXACTLY what
                      a crew lead's portal renders (real page, real API
                      payload, read-only) for any crew member + any date.
                      Also reachable per-lead via the 👁 that appears when
                      hovering a crew name on the board's left rail. */}
                  <button onClick={() => setPortalPreview({ crewId: '', date: format(new Date(), 'yyyy-MM-dd') })}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 whitespace-nowrap"
                    title="Open a read-only copy of a crew member's portal — exactly what they see on their phone">
                    👁 Portal Preview
                  </button>
                </>
              )}
              {/* TV-mode toggle. Strips chrome (right Unassigned panel,
                  header buttons, title subtitle, outer padding) so the
                  board takes the full screen on a big display. Only
                  shown when fullscreen is active at 2wk+ — that's the
                  only context where toggling it does anything useful,
                  and hiding it elsewhere keeps the office toolbar tidy. */}
              {isFullscreen && zoomWeeks >= 2 && (
                <IconButton
                  label={tvMode ? 'TV mode on — click to disable' : 'TV mode off — click to enable big-display layout'}
                  active={tvMode}
                  aria-pressed={tvMode}
                  onClick={toggleTvMode}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                    <line x1="8" y1="21" x2="16" y2="21"/>
                    <line x1="12" y1="17" x2="12" y2="21"/>
                  </svg>
                </IconButton>
              )}
              {narrowRail && (
                <>
                  <button
                    ref={railBtnRef}
                    type="button"
                    onClick={() => setRailOpen(o => !o)}
                    aria-expanded={railOpen}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-bold border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
                    title="Unassigned jobs — the rail folds in here when the window is narrow"
                  >
                    Unassigned
                    {allUnassigned.length > 0 && <span className="bg-red-600 text-white text-[10px] px-1.5 py-0.5 rounded-full leading-none">{allUnassigned.length}</span>}
                  </button>
                  <PopoverPanel open={railOpen} onClose={() => setRailOpen(false)} anchorRef={railBtnRef} width={320} align="right" ariaLabel="Unassigned jobs" className="max-h-[70vh] overflow-auto flex flex-col">
                    {unassignedRail}
                  </PopoverPanel>
                </>
              )}
              <IconButton label={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'} onClick={toggleFullscreen}>
                {isFullscreen
                  ? <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 4 20 10 20"/><polyline points="20 10 20 4 14 4"/><line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                  : <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                }
              </IconButton>
            </div>
          </div>
        </div>
        {/* Grid + rail share one row under the header, so the rail's top
            lines up with the grid (Jaylen 2026-09-11: in dark mode it "looked
            detached"). Rail width flexes 200–288px with the board. */}
        <div className="flex flex-1 min-h-0 min-w-0">
        <div className="flex-1 flex flex-col min-w-0">
        {narrowDay && (
          <div className="flex gap-1 overflow-x-auto pb-2 -mx-1 px-1" role="tablist" aria-label="Day">
            {weekDays.map((d, i) => {
              const on = i === narrowIdx;
              return (
                <button key={i} type="button" role="tab" aria-selected={on} onClick={() => setNarrowDayIdx(i)}
                  className={`flex-shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-bold border whitespace-nowrap ${on ? 'bg-slate-900 text-white border-slate-900' : isToday(d) ? 'bg-blue-50 text-blue-800 border-blue-200' : 'bg-white text-slate-600 border-slate-200'}`}>
                  {baseDayLabels[i % 7]} {format(d, 'd')}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex-1 overflow-auto bg-white rounded-lg border border-slate-200 relative">
          <div className={`dispatch-grid ${zoomWeeks >= 2 ? 'dispatch-compact' : ''} ${floorActive ? `dispatch-floor dp-tier-${pillTier}` : ''} ${narrowDay ? 'dispatch-narrow-day' : ''}`} style={{ gridTemplateColumns: `${narrowDay ? '120px' : zoomWeeks >= 3 ? '90px' : zoomWeeks >= 2 ? '110px' : '160px'} repeat(${totalDays}, minmax(${floorActive ? `${colFloor}px` : '0'}, 1fr))`, tableLayout: 'fixed', minWidth: floorActive ? `${crewColW + totalDays * colFloor}px` : undefined }}>
            {/* Header row is sticky inside the board's own scroll container
                (Jaylen 2026-09-25: "when scrolling the date row stayed
                pinned") — the grid scrolls, the dates don't. z-30 keeps it
                over pills; an expanded pill's cell lifts to 40/60 and may
                overlap it on purpose. */}
            <div className="dispatch-cell dispatch-crew-hd sticky top-0 z-30 bg-slate-100 font-bold text-sm text-slate-700 border-r border-b border-slate-300 flex items-center justify-between" style={{cursor:'default', padding: '8px 10px'}}>
              <span className="text-xs">Crew</span>
              <IconButton label="Reorder crew" size="sm" className="-my-2 -mr-1" onClick={() => setCrewReorderModal(true)}>
                <IconEdit size={12}/>
              </IconButton>
            </div>
            {days.map((day, idx) => {
              const isWeekBoundary = zoomWeeks > 1 && idx > 0 && idx % 7 === 0;
              const dayIso = format(day, 'yyyy-MM-dd');
              const isDispatched = dispatchedDates.has(dayIso);
              const changeCount = changesByDate[dayIso] || 0;
              // Passive per-day state (2026-07-29 button reconciliation:
              // the day-send BUTTON moved to the toolbar's 📋 Daily
              // Dispatch — headers only report status now):
              //  • "✓ Sent"        — day released; click resends via the
              //                      Daily Dispatch modal.
              //  • amber "↻ N changed" — office edits landed AFTER the
              //                      send (dispatch_change_log). Click
              //                      opens the changes-since-send panel.
              // Past days drop the chip once the date is behind us —
              // stale-lineup awareness only matters today and forward.
              const dayDateOnly = new Date(day.getFullYear(), day.getMonth(), day.getDate()).getTime();
              const isPast = dayDateOnly < todayDateOnly;
              const showChanges = !isPast && isDispatched && changeCount > 0;
              const showSent = !isPast && isDispatched && changeCount === 0;
              const dayFlag = flagByDate.get(dayIso) || null;
              return (
                <div key={idx} className={`dispatch-cell sticky top-0 z-30 font-bold text-center border-b border-slate-300 ${isToday(day) ? 'dispatch-today' : ''} bg-slate-100`}
                  style={{ cursor: 'default', ...(isWeekBoundary ? { borderLeft: '3px solid #94a3b8' } : {}) }}>
                  <div className={`text-slate-700 ${zoomWeeks >= 2 ? 'text-[10px]' : 'text-xs'}`}>{dayLabels[idx]}</div>
                  <div className={`text-slate-900 ${zoomWeeks >= 3 ? 'text-[10px]' : zoomWeeks >= 2 ? 'text-[11px]' : 'text-sm'}`}>{format(day, zoomWeeks >= 2 ? 'MMM d' : 'MMM d')}</div>
                  {/* Day flag chip (day_flags, 2026-09-18): FULL = a lead said
                      stop selling this day; CLOSED = holiday / break. Edited
                      on the Capacity page, only shown here. */}
                  {dayFlag && (
                    <span
                      className={`inline-block mt-0.5 text-[9px] font-extrabold tracking-wider px-1.5 py-px rounded ${dayFlag.kind === 'closed' ? 'bg-slate-300 text-slate-700' : 'bg-red-100 text-red-800'}`}
                      title={dayFlag.note || (dayFlag.kind === 'closed' ? 'Closed' : 'Full — no more jobs')}
                    >
                      {dayFlag.kind === 'closed' ? 'CLOSED' : 'FULL'}
                    </span>
                  )}
                  {showChanges ? (
                    <button
                      type="button"
                      onClick={() => setChangesModal(dayIso)}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-400 text-amber-950 hover:bg-amber-500"
                      title={`${changeCount} change${changeCount === 1 ? '' : 's'} since this day's dispatch was sent — click to review`}
                    >
                      ↻ {changeCount} changed
                    </button>
                  ) : showSent ? (
                    <button
                      type="button"
                      onClick={() => setDailyModal(dayIso)}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md bg-slate-200 text-slate-600 hover:bg-slate-300"
                      title="Daily dispatch already sent — click to resend"
                    >
                      ✓ Sent
                    </button>
                  ) : null}
                </div>
              );
            })}

            {/* Single Unassigned row — every dated unassigned pill lands
                here, and each pill carries its own PHASE_KIND badge so the
                dispatcher can see at a glance whether it's a PACK / LOAD /
                DRIVE / UNLOAD / HOLD / RETURN / TRASH leg. Walkthroughs and
                new-estimate / new-client drafts show no phase badge. */}
            {/* Always show the Unassigned row so dispatchers can drop jobs here */}
              <React.Fragment>
                <div className="dispatch-crew-cell dispatch-unassigned border-b-2 border-amber-300" style={{cursor:'default'}}>
                  <div className="w-2.5 h-8 rounded-full bg-amber-400 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold text-amber-800 truncate">Unassigned</span>
                      {unassignedInWeek.length > 0 && <span className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0">{unassignedInWeek.length}</span>}
                    </div>
                    {/* Past walkthroughs resolve list (2026-08-07) — its own
                        line under the label so the narrow crew cell never
                        clips it. */}
                    {pastWtJobs.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setPastWtModal(true)}
                        className="mt-0.5 bg-amber-400 hover:bg-amber-300 text-amber-950 text-[10px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap"
                        title={`${pastWtJobs.length} past walkthrough${pastWtJobs.length === 1 ? '' : 's'} with no recorded outcome — click to resolve`}>
                        ⚠ {pastWtJobs.length} past
                      </button>
                    )}
                  </div>
                </div>
                {weekDates.map((dateStr, dayIdx) => {
                  const dayUnassigned = unassignedInWeek.filter(item => item.move_date === dateStr);
                  // Dated holds whose loader isn't on the board yet — a chip
                  // here rather than nowhere (they're not pool items).
                  const orphanGhosts = ghostHoldsFor(null, dateStr);
                  // An expanded unassigned pill lifts its cell like the crew
                  // cells do, so the popup paints over the rows below.
                  const cellHasExpandedUn = !!expandedPill && dayUnassigned.some(item => item._source !== 'estimate' && item._source !== 'client' && expandedPill === 'un:' + item.id + dateStr);
                  return (
                    <div key={`unassigned-${dateStr}`}
                      className={`dispatch-cell dispatch-unassigned relative border-b-2 border-amber-300 ${isToday(days[dayIdx]) ? 'dispatch-today' : ''}`}
                      style={{ minHeight: dayUnassigned.length > 0 ? '60px' : undefined, ...(zoomWeeks > 1 && dayIdx > 0 && dayIdx % 7 === 0 ? { borderLeft: '3px solid #94a3b8' } : {}), ...(cellHasExpandedUn ? { zIndex: 40 } : {}) }}
                      onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                      onDrop={handleUnassignDrop}
                    >
                      {dayUnassigned.map(item => {
                        const isEstEntry = item._source === 'estimate';
                        const isClientEntry = item._source === 'client';
                        const itemClient = isClientEntry || isEstEntry ? { name: item._clientName } : getClient(item.client_id);
                        const itemKey = isClientEntry ? 'ucl-' + item._clientId : isEstEntry ? 'ue-' + item._estimateId : 'uj-' + item.id;
                        const stateAbbr = getStateAbbr(item.to_address);
                        const isOOS = stateAbbr && stateAbbr !== 'NC';
                        const pillBg = 'bg-slate-500';
                        // Tentative move date (moves rule 3) → dashed amber edge, same as the crew-row pills.
                        const itemTentative = !isEstEntry && !isClientEntry && tentativeFor(item);
                        const pillBorder = 'border-slate-600';
                        const phaseBadge = !isEstEntry && !isClientEntry && item.type !== 'WALKTHROUGH'
                          ? kindBadge(item.phase_kind || 'move')
                          : null;
                        // Real job rows get the same hover card + click-to-expand
                        // as crew-row pills (Jaylen 2026-09-25: "hover/clicking
                        // on unassigned pills does nothing"). Estimate / client
                        // drafts aren't jobs yet — drag-only, as before.
                        const isJobEntry = !isEstEntry && !isClientEntry;
                        const unKey = 'un:' + item.id + dateStr;
                        const unExpanded = isJobEntry && expandedPill === unKey;
                        return (
                          <div key={itemKey} className="relative mb-1" style={unExpanded ? { zIndex: 60 } : undefined}>
                          <div
                            draggable
                            onDragStart={(e) => {
                              if (isEstEntry) {
                                setDragJob({ fromPanel: true, fromDate: item.move_date, isNewEstimate: true, estimateData: item });
                              } else if (isClientEntry) {
                                setDragJob({ fromPanel: true, fromDate: item.move_date, isNewClient: true, clientData: item });
                              } else {
                                handlePanelDragStart(e, item);
                              }
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData('text/plain', itemKey);
                            }}
                            onDragEnd={() => { setDragJob(null); setDragOver(null); snapBackZoom(); }}
                            onClick={isJobEntry ? (e) => { e.stopPropagation(); setHoverPill(null); setExpandedPill(unExpanded ? null : unKey); } : undefined}
                            onMouseEnter={isJobEntry ? (e) => showHoverCard(e, item, dateStr) : undefined}
                            onMouseLeave={isJobEntry ? () => setHoverPill(null) : undefined}
                            className={`${pillBg} text-white ${zoomWeeks >= 3 ? 'px-1 py-0.5 rounded text-[9px]' : zoomWeeks >= 2 ? 'px-[3px] py-1 rounded-lg text-[10px]' : 'px-2 py-1.5 rounded-lg text-xs'} ${isJobEntry ? 'cursor-pointer' : 'cursor-grab'} active:cursor-grabbing border ${pillBorder} shadow-sm hover:shadow-md transition-shadow ${unExpanded ? 'ring-2 ring-amber-400' : ''}`}
                            title={itemTentative ? 'Move date is still tentative' : undefined}
                            style={{ borderLeft: '3px solid rgba(255,255,255,0.4)', ...(itemTentative ? { outline: '2px dashed #f59e0b', outlineOffset: '-2px' } : {}) }}
                          >
                            {item.type === 'WALKTHROUGH' ? (
                              (() => {
                                const wtStreet = (item.from_address || itemClient?.origin_address || '').split(',')[0]?.trim() || '';
                                const wtFirst = itemClient?.name?.split(' ')[0] || '?';
                                // Client-completed (today's, still unassigned) shows
                                // ✓ Completed like the crew-row pills; it auto-drops
                                // from the rail tomorrow. Past UNANSWERED walkthroughs
                                // never render here — they live in the "⚠ N past"
                                // resolve list on the row header.
                                const railWtDone = !isEstEntry && !isClientEntry
                                  && getClient(item.client_id)?.walkthrough_status === 'completed';
                                // Same family as the MOVE pill (Jaylen 2026-09-25): a
                                // WALKTHROUGH badge, the name, then time · street —
                                // or ✓ Completed — underneath.
                                return (
                                  <React.Fragment>
                                    {/* Badge on its own line (Jaylen: "no one uses the
                                        WT abbreviation") — the full word never fights
                                        the name for room. Sized from the MEASURED
                                        column (dayColWidth): as big as the column
                                        allows, one step down as it narrows. */}
                                    <div className="flex items-center">
                                      <span className={`inline-block max-w-full truncate rounded ${zoomWeeks >= 2 ? (dayColWidth >= 104 ? 'px-1 text-[9px] font-bold tracking-normal' : dayColWidth >= 92 ? 'px-1 text-[8.5px] font-bold tracking-normal' : 'px-0.5 text-[8px] font-bold tracking-tight') : 'px-1.5 text-[10px] font-black tracking-wide'}`} style={{ background: 'rgba(196,181,253,0.9)', color: '#4c1d95' }}>WALKTHROUGH</span>
                                    </div>
                                    <div className="font-bold text-xs mt-0.5 truncate">{wtFirst}{itemClient?.virtual_walkthrough ? ' · Virtual' : ''}</div>
                                    <div className="opacity-90 text-[11px] font-semibold mt-0.5 truncate">
                                      {railWtDone
                                        ? <span className="inline-flex items-center gap-1"><span className="bg-white/30 rounded px-1">✓</span>Completed</span>
                                        : `${item.time ? formatTime12(item.time) : ''}${item.time && wtStreet ? ' · ' : ''}${wtStreet}`}
                                    </div>
                                  </React.Fragment>
                                );
                              })()
                            ) : (
                              <React.Fragment>
                                <div className="flex items-center gap-1 font-bold text-xs truncate">
                                  {SERVICE_TYPE_CHIP[item.type] ? (
                                    <span className="px-1 rounded text-[9px] font-black tracking-wider bg-slate-700 text-white">
                                      {SERVICE_TYPE_CHIP[item.type]}
                                    </span>
                                  ) : phaseBadge && (
                                    <span
                                      className="px-1 rounded text-[9px] font-black tracking-wider"
                                      style={{ background: phaseBadge.bg, color: phaseBadge.fg }}
                                    >
                                      {phaseBadge.label}
                                    </span>
                                  )}
                                  {isEstEntry && <span className="bg-white/30 px-1 rounded text-[9px] font-bold">EST</span>}
                                  {isClientEntry && <span className="bg-white/30 px-1 rounded text-[9px] font-bold">NEW</span>}
                                  <span className="truncate">{itemClient?.name || '?'}</span>
                                  {isOOS && <span className="bg-white/30 px-1 rounded text-[9px] ml-auto font-bold">{stateAbbr}</span>}
                                </div>
                                <div className="opacity-90 text-[11px] font-semibold mt-0.5">
                                  {itemTentative ? '~' : ''}{item.time ? formatTime12(item.time) : ''} · {(item.job_label || item.type || '').replace(/_/g, ' ').toUpperCase()}
                                </div>
                              </React.Fragment>
                            )}
                          </div>
                          {unExpanded && (
                            <DispatchPillExpanded
                              job={item} client={getClient(item.client_id)} isSpanDay={false} dateStr={dateStr} spanDayNum={null}
                              getCrew={getCrew} getTruck={getTruck} getTripTag={getTripTag} getLegCrew={getLegCrew}
                              estimates={estimates}
                              allJobs={jobs}
                              onClose={() => setExpandedPill(null)}
                              onEditAssignment={(mode) => { setExpandedPill(null); setAssignModal({ ...item, _assignMode: mode || 'all' }); }}
                              onSendSms={() => { setExpandedPill(null); setSmsConfirm(item); }}
                              onNavigateToClient={(clientId) => { setExpandedPill(null); nav.navigateToClient(clientId); }}
                              saveJob={saveJob}
                              showToast={showToast}
                              align={dayIdx >= totalDays / 2 ? 'right' : 'left'}
                            />
                          )}
                          </div>
                        );
                      })}
                      {orphanGhosts.map(g => renderGhostHold(g, 'unassigned'))}
                    </div>
                  );
                })}
              </React.Fragment>

            {/* Crew rows */}
            {activeCrew.map((crewMember) => {
              return (
                <React.Fragment key={crewMember.id}>
                  <div className="dispatch-crew-cell group" style={{cursor:'default'}}>
                    <span className={`flex-1 truncate font-bold ${zoomWeeks >= 3 ? 'text-sm' : zoomWeeks >= 2 ? 'text-base' : 'text-lg'}`}
                          style={{ color: crewMember.display_color }}
                          title={crewMember.name}>
                      {crewShortName(crewMember.name)}
                    </span>
                    {/* Portal Preview shortcut (2026-08-06): hover the name →
                        👁 opens THIS crew member's portal, read-only, for
                        today (or the shown week's first day when browsing
                        another week). Toolbar button = any crew, any date. */}
                    <button
                      type="button"
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-slate-700 text-sm flex-shrink-0 px-1"
                      title={`Preview ${crewMember.name}'s crew portal`}
                      onClick={(e) => {
                        e.stopPropagation();
                        const todayIso = format(new Date(), 'yyyy-MM-dd');
                        openPortalPreview(crewMember.id, weekDates.includes(todayIso) ? todayIso : weekDates[0]);
                      }}>
                      👁
                    </button>
                  </div>
                  {weekDates.map((dateStr, dayIdx) => {
                    const cellJobs = getJobsForCrewDay(crewMember.id, dateStr);
                    const cellGhosts = ghostHoldsFor(crewMember.id, dateStr);
                    const day = days[dayIdx];
                    const avail = getAvail(crewMember.id, dateStr);
                    const isCellMenu = cellMenu && cellMenu.crewId === crewMember.id && cellMenu.dateStr === dateStr;
                    // Lift the cell's stacking context when any of its pills is
                    // expanded, so the absolute-positioned popover paints above
                    // sibling cells (the cell uses `relative` for pill anchoring,
                    // which creates a per-cell stacking context — the popover's
                    // z-index alone can't escape that).
                    const cellHasExpandedPill = !!expandedPill && (
                      cellJobs.some(j => expandedPill === j.id + crewMember.id + dateStr)
                      || cellGhosts.some(g => expandedPill === 'ghost:' + g.job.id + crewMember.id + dateStr)
                    );

                    return (
                      <div key={`${crewMember.id}-${dateStr}`}
                        className={`dispatch-cell ${isToday(day) ? 'dispatch-today' : ''} relative ${dragOver === dateStr + crewMember.id ? 'ring-2 ring-inset ring-blue-400 bg-blue-50' : ''}`}
                        style={{
                            ...(zoomWeeks > 1 && dayIdx > 0 && dayIdx % 7 === 0 ? { borderLeft: '3px solid #94a3b8' } : null),
                            ...(cellHasExpandedPill ? { zIndex: 40 } : null),
                        }}
                        onClick={() => { const realJobs = cellJobs.filter(j => !(j._isSpanDay && getTripTag(j.trip_tags, dateStr) === 'Hold')); if (realJobs.length === 0) handleCellClick(crewMember.id, dateStr); }}
                        onContextMenu={(e) => handleCellRightClick(e, crewMember.id, dateStr)}
                        onDragOver={(e) => { if (dragJob) handleDragOver(e, dateStr + crewMember.id); }}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => { if (dragJob) handleDrop(e, dateStr, crewMember.id); }}>

                        {/* Availability indicator */}
                        {(() => {
                          const hasHold = cellGhosts.length > 0 || cellJobs.some(j => j._isSpanDay && getTripTag(j.trip_tags, dateStr) === 'Hold');
                          const realJobs = cellJobs.filter(j => !(j._isSpanDay && getTripTag(j.trip_tags, dateStr) === 'Hold'));
                          if (realJobs.length > 0) return null;
                          if (avail) return (
                            <div className={`flex items-center justify-center ${hasHold ? '' : 'h-full'} text-xs font-bold cursor-pointer ${
                              avail.status === 'off_requested' ? '' : avail.status === 'scheduled_off' ? 'text-black' : 'text-slate-900'
                            }`} style={{ color: avail.status === 'off_requested' ? crewMember.display_color : undefined }}
                              title={avail.note ? `Off: ${avail.note}` : undefined}>
                              {avail.status === 'off_requested' ? 'OFF' :
                               avail.status === 'scheduled_off' ? '—' :
                               avail.status === 'on_call' ? 'On Call' : 'Office'}
                            </div>
                          );
                          // Recurring weekly day off (sql/077, crewOff.js) —
                          // no availability row exists; the OFF is virtual.
                          if ((crewMember.recurring_days_off || []).includes(day.getDay())) return (
                            <div className={`flex items-center justify-center ${hasHold ? '' : 'h-full'} text-xs font-bold cursor-pointer`}
                              style={{ color: crewMember.display_color }} title="Weekly day off (set on the Crew page)">
                              OFF
                            </div>
                          );
                          return <div className={`flex items-center justify-center ${hasHold ? '' : 'h-full'} text-slate-300 text-sm`}>—</div>;
                        })()}

                        {/* Context menu */}
                        {isCellMenu && (
                          <div className="absolute left-0 top-0 z-40 bg-white border border-slate-300 rounded-lg shadow-xl p-2 w-44" onClick={e => e.stopPropagation()}>
                            <div className="text-xs text-slate-500 px-2 py-1 font-medium border-b border-slate-100 mb-1">{crewMember.name} — {format(parseISO(dateStr), 'EEE MMM d')}</div>
                            <button className="w-full text-left text-xs px-2 py-1.5 hover:bg-red-50 rounded flex items-center gap-2" onClick={() => handleSetAvailability(crewMember.id, dateStr, avail?.status === 'off_requested' ? null : 'off_requested')}>
                              <span className="font-bold" style={{ color: crewMember.display_color }}>OFF</span> {avail?.status === 'off_requested' ? '(clear)' : 'Requested Off'}
                            </button>
                            <button className="w-full text-left text-xs px-2 py-1.5 hover:bg-slate-50 rounded flex items-center gap-2" onClick={() => handleSetAvailability(crewMember.id, dateStr, avail?.status === 'scheduled_off' ? null : 'scheduled_off')}>
                              <span className="font-black text-black text-base leading-none">—</span> {avail?.status === 'scheduled_off' ? '(clear)' : 'Scheduled Off'}
                            </button>
                            <button className="w-full text-left text-xs px-2 py-1.5 hover:bg-amber-50 rounded flex items-center gap-2" onClick={() => handleSetAvailability(crewMember.id, dateStr, avail?.status === 'on_call' ? null : 'on_call')}>
                              <span className="text-amber-600">●</span> {avail?.status === 'on_call' ? 'Clear On Call' : 'On Call'}
                            </button>
                            <button className="w-full text-left text-xs px-2 py-1.5 hover:bg-blue-50 rounded flex items-center gap-2" onClick={() => handleSetAvailability(crewMember.id, dateStr, avail?.status === 'office' ? null : 'office')}>
                              <span className="text-blue-600">■</span> {avail?.status === 'office' ? 'Clear Office' : 'Office'}
                            </button>
                            {avail && <button className="w-full text-left text-xs px-2 py-1.5 hover:bg-slate-100 rounded text-slate-500 mt-1 border-t border-slate-100" onClick={() => handleSetAvailability(crewMember.id, dateStr, null)}>
                              Clear Status
                            </button>}
                            <button className="w-full text-left text-xs px-2 py-1.5 hover:bg-slate-100 rounded text-slate-400 mt-1 border-t border-slate-100" onClick={() => setCellMenu(null)}>Cancel</button>
                          </div>
                        )}

                        {/* Job pills */}
                        {cellJobs.filter(j => !(j._isSpanDay && getTripTag(j.trip_tags, dateStr) === 'Hold')).map((job, jobIdx) => {
                          const client = getClient(job.client_id);
                          const isExpanded = expandedPill === job.id + crewMember.id + dateStr;
                          const isSpanDay = job._isSpanDay;
                          const spanDayNum = job._spanDayNum;
                          const jobDuration = job.duration_days || 1;
                          const isMultiDay = jobDuration > 1;
                          const legCrew = isSpanDay ? getLegCrew(job.trip_tags, dateStr) : null;
                          const tripLeadId = legCrew && legCrew.length > 0 ? (legCrew.includes(job.leader_id) ? job.leader_id : legCrew[0]) : null;
                          const leaderCrew = getCrew(tripLeadId || job.leader_id);
                          const isWalkthrough = job.type === 'WALKTHROUGH';
                          // Completed walkthrough (Jaylen 2026-08-07):
                          // EITHER record counts — the client's
                          // walkthrough_status (estimator app) OR the job
                          // row's status (board Complete button, which now
                          // stamps both). One unified ✓ Done treatment; the
                          // rotated COMPLETED stamp is moves-only.
                          // (job.status checked directly — isCompleted is
                          // declared a few lines below this.)
                          const wtDone = isWalkthrough && (client?.walkthrough_status === 'completed' || job.status === 'completed');
                          const pillColor = leaderCrew?.display_color || '#6b7280';
                          const stateAbbr = getStateAbbr(job.to_address);
                          const isOutOfState = stateAbbr && stateAbbr !== 'NC';
                          const isCompleted = job.status === 'completed';
                          // In-progress gets a visible cue (QA M3 OBS-1) — it's
                          // the status a crew holds DURING the move, and it used
                          // to render identically to merely-assigned.
                          const isInProgress = job.status === 'in_progress';
                          // Tentative move date (moves rule 3) → dashed amber edge.
                          const isTentative = tentativeFor(job);
                          // Sibling linking: for multi-phase estimates we show an "N/total"
                          // chip (e.g. 2/3) so dispatchers can instantly see that a DRIVE
                          // pill is the second of three chunks belonging to the same trip.
                          // We compute this from all move_jobs rows sharing the estimate_id.
                          const siblingInfo = (() => {
                            if (!job.estimate_id || !job.phase_sequence) return null;
                            const siblings = jobs
                              .filter(j => j.estimate_id === job.estimate_id && j.phase_sequence)
                              .sort((a, b) => (a.phase_sequence || 0) - (b.phase_sequence || 0));
                            if (siblings.length <= 1) return null;
                            const idx = siblings.findIndex(s => s.id === job.id);
                            if (idx < 0) return null;
                            return { index: idx + 1, total: siblings.length };
                          })();

                          return (
                            // dispatch-pill-enter: applies the stagger fade-in (Phase 6).
                            // The animation only runs once on mount because keyframes
                            // forwards-fill; status changes don't re-trigger it.
                            //
                            // When expanded, lift the wrapper's z-index so the
                            // DispatchPillExpanded popup (which floats below
                            // the pill via absolute positioning) stacks above
                            // adjacent cells' pills. Without this the popup
                            // bleeds visually with sibling-row pills.
                            <div
                              key={job.id}
                              className={`mb-1 relative dispatch-pill-enter ${zoomWeeks >= 2 ? 'dispatch-pill-compact' : ''}`}
                              style={isExpanded ? { zIndex: 60 } : undefined}
                            >
                              {isCompleted && !isWalkthrough && (
                                // overflow-hidden clips the rotated label so it
                                // stays inside the pill bounds — at 2wk+ zoom
                                // the un-clipped "COMPLETED" was bleeding across
                                // adjacent cells. Drop tracking + shrink text at
                                // narrow zooms so the word actually fits.
                                <div className="absolute inset-0 bg-white/50 rounded-lg z-10 flex items-center justify-center pointer-events-none overflow-hidden">
                                  <span className={`font-black text-slate-500 uppercase rotate-[-8deg] ${zoomWeeks >= 3 ? 'text-[7px] tracking-tight' : zoomWeeks >= 2 ? 'text-[8px] tracking-wide' : 'text-[10px] tracking-widest'}`}>
                                    {zoomWeeks >= 3 ? '✓' : 'Completed'}
                                  </span>
                                </div>
                              )}
                              {/* Live cue for in-progress jobs (QA M3 OBS-1):
                                  pulsing cyan dot + ring, clears when the job
                                  completes. Reduced-motion gate in index.css
                                  covers the pulse. */}
                              {isInProgress && <span className="dispatch-pill-live" title="In progress" aria-hidden="true" />}
                              {/* dispatch-pill: smooth color transition when status changes.
                                  pillColor is fed via --pill-color so the transition can
                                  animate between two named values. */}
                              <div className={`dispatch-pill text-white cursor-pointer ${isSpanDay ? 'rounded-r-lg border-l-4 border-white/40' : isMultiDay ? 'rounded-l-lg rounded-r-none' : (zoomWeeks >= 3 && !tvMode ? 'rounded-sm' : 'rounded-lg')} ${(isCompleted || wtDone) ? 'opacity-60' : ''} ${isInProgress ? 'dispatch-pill-inprogress' : ''}`}
                                title={isTentative ? 'Move date is still tentative' : undefined}
                                // Tentative = dashed amber OUTLINE (inset) so the pill keeps its size and colour (moves rule 3, 2026-09-18).
                                style={{ '--pill-color': pillColor, padding: zoomWeeks >= 3 ? (tvMode ? '3px 5px' : '3px 4px') : zoomWeeks >= 2 ? '4px 6px' : (isMultiDay || isSpanDay ? '8px 10px' : '6px 8px'), minWidth: 0, ...(isTentative ? { outline: '2px dashed #f59e0b', outlineOffset: '-2px' } : {}) }}
                                draggable={!isSpanDay && !isCompleted}
                                onDragStart={!isCompleted ? (e) => { setHoverPill(null); handleDragStart(e, job, dateStr, crewMember.id); } : undefined}
                                onDragEnd={!isCompleted ? () => { setDragJob(null); setDragOver(null); snapBackZoom(); } : undefined}
                                onClick={(e) => { e.stopPropagation(); setHoverPill(null); setExpandedPill(isExpanded ? null : job.id + crewMember.id + dateStr); }}
                                // Hover card (2026-08-07): instant crew/truck
                                // summary at every zoom — replaced the old
                                // 3wk-only native title (slow + unstyled).
                                onMouseEnter={(e) => showHoverCard(e, job, dateStr)}
                                onMouseLeave={() => setHoverPill(null)}>
                                {isMultiDay && !isSpanDay ? (
                                  <React.Fragment>
                                    <div className="flex items-center gap-1.5 mb-1">
                                      <span className="font-black text-sm tracking-wide" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>LOAD</span>
                                      <span className="bg-white/30 px-1.5 py-0.5 rounded text-[10px] font-bold ml-auto">{jobDuration}d</span>
                                    </div>
                                    <div className="font-bold text-sm">{client?.name?.split(' ')[0] || '?'} → {stateAbbr || '?'}</div>
                                    {(job.truck_ids || []).length > 0 && (
                                      <div className="flex flex-wrap gap-0.5 mt-1">
                                        {job.truck_ids.map(tid => { const truck = getTruck(tid); const conflict = isTruckConflict(tid, dateStr, job.id, job.leader_id); return <span key={tid} className={`px-1.5 py-0.5 rounded text-[10px] ${conflict ? 'bg-red-500 text-white font-bold ring-1 ring-red-300' : 'bg-black/20'}`} title={conflict ? `⚠ ${truck?.name} is double-booked on this day` : ''}>{truck?.name}{conflict ? ' ⚠' : ''}</span>; })}
                                      </div>
                                    )}
                                  </React.Fragment>
                                ) : isSpanDay ? (
                                  (() => {
                                    const tripTags = job.trip_tags || {};
                                    const tagForDay = getTripTag(tripTags, dateStr);
                                    const clientFirst = client?.name?.split(' ')[0] || '?';
                                    const destState = stateAbbr || '?';
                                    const destCity = job.to_address ? job.to_address.split(',').slice(-2, -1)[0]?.trim() || '' : '';
                                    return (
                                      <React.Fragment>
                                        <div className="flex items-center gap-1.5 mb-1">
                                          <span className="font-black text-sm tracking-wide" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{tagForDay ? tagForDay.toUpperCase() : `DAY ${spanDayNum}`}</span>
                                        </div>
                                        <div className="font-bold text-sm">{clientFirst} → {destState}</div>
                                        {destCity && <div className="opacity-70 text-[10px] mt-0.5">{destCity}, {destState}</div>}
                                      </React.Fragment>
                                    );
                                  })()
                                ) : (
                                  isWalkthrough ? (
                                    (() => {
                                      const wtStreet = (job.from_address || client?.origin_address || '').split(',')[0]?.trim() || '';
                                      const wtFirst = client?.name?.split(' ')[0] || '?';
                                      const compact = cellJobs.length > 1;
                                      // Capacity-block view at 3wk/4wk laptop:
                                      // walkthrough collapses to the same plain
                                      // colored block as the rest. Hover tooltip
                                      // on the outer pill carries the details.
                                      if (zoomWeeks >= 3 && !tvMode) {
                                        return (
                                          <div className="w-full" style={{ minHeight: '18px' }} aria-label={`Walkthrough — ${wtFirst}`} />
                                        );
                                      }
                                      if (compact) {
                                        return (
                                          <React.Fragment>
                                            <div className="dp-l1 flex items-baseline gap-1 text-[11px] leading-tight min-w-0">
                                              <span className="opacity-70">{jobIdx + 1}.</span>
                                              <span className="font-semibold truncate"><span className="dp-full">Walkthrough</span><span className="dp-abbr">WT</span>{wtDone ? ' ✓' : ''}</span>
                                              <span className="dp-st opacity-90 ml-auto whitespace-nowrap">{wtDone ? 'Done' : formatTime12(job.time)}</span>
                                            </div>
                                            <div className="font-bold text-[11px] truncate leading-tight">{wtFirst}{client?.virtual_walkthrough ? ' · Virtual' : ''}</div>
                                          </React.Fragment>
                                        );
                                      }
                                      return (
                                        <React.Fragment>
                                          <div className="dp-l1 flex items-baseline gap-1.5 text-xs min-w-0">
                                            <span className="font-semibold truncate"><span className="dp-full">Walkthrough</span><span className="dp-abbr">WT</span></span>
                                            {wtDone
                                              ? <span className="font-bold ml-auto whitespace-nowrap bg-white/25 rounded px-1.5">✓ Done</span>
                                              : <span className="font-bold ml-auto whitespace-nowrap">{formatTime12(job.time)}</span>}
                                          </div>
                                          <div className="font-bold text-xs mt-0.5 truncate">{wtFirst}{client?.virtual_walkthrough ? ' · Virtual' : ''}</div>
                                          {wtStreet && <div className="opacity-80 text-[10px] truncate">{wtStreet}</div>}
                                        </React.Fragment>
                                      );
                                    })()
                                  ) : (
                                  (() => {
                                    // Pill rendering modes:
                                    //   office     = 1wk/2wk laptop: full pill.
                                    //   capacity   = 3wk/4wk laptop (no TV): the
                                    //                pill becomes a slim colored
                                    //                block — at this zoom the
                                    //                dispatcher is scanning for
                                    //                "who's busy when," not
                                    //                reading individual names.
                                    //   tvPillRich = 3wk/4wk + TV mode on: full
                                    //                labels, first name only,
                                    //                small text.
                                    // First name everywhere at 2wk+.
                                    const tvPillRich = zoomWeeks >= 3 && tvMode;
                                    const compactMode = zoomWeeks >= 3 && !tvMode;
                                    const useFirstNameOnly = zoomWeeks >= 2;

                                    // ── Capacity block (3wk/4wk laptop) ──
                                    // Drop all text — the pill background color
                                    // already conveys the phase. Tiny sibling
                                    // counter overlays on multi-leg trips so a
                                    // 5-leg OOS still reads as a sequence.
                                    // Full info shows via title tooltip on the
                                    // outer pill (set below — search for
                                    // `pillHoverTitle`). Block is sized to feel
                                    // like a tangible square: ~18px tall, fills
                                    // the cell width.
                                    if (compactMode) {
                                      return (
                                        <div
                                          className="flex items-center justify-end gap-0.5 text-white w-full"
                                          style={{ minHeight: '18px' }}
                                        >
                                          {siblingInfo && siblingInfo.total > 1 && (
                                            <span className="text-[9px] font-bold opacity-95 leading-none">
                                              {siblingInfo.index}/{siblingInfo.total}
                                            </span>
                                          )}
                                          {(!siblingInfo || siblingInfo.total <= 1) && isOutOfState && stateAbbr && (
                                            <span className="text-[9px] font-bold opacity-85 leading-none">
                                              {stateAbbr}
                                            </span>
                                          )}
                                        </div>
                                      );
                                    }

                                    return (
                                    <React.Fragment>
                                      {/* Line 1: Phase badge + sibling counter + state.
                                          Compact mode (3wk+ laptop) uses a single
                                          letter chip; everywhere else keeps the
                                          full word. */}
                                      <div className={`dp-l1 flex items-center gap-0.5 min-w-0 ${tvPillRich ? 'text-[8px]' : zoomWeeks >= 2 ? 'text-[9px]' : 'text-[10px]'}`}
                                           title={compactMode && job.phase_kind ? kindBadge(job.phase_kind).label : ''}>
                                        {cellJobs.length > 1 && <span className="opacity-70 font-medium">{jobIdx + 1}.</span>}
                                        {job.phase_kind && job.phase_kind !== 'move' && (
                                          <span
                                            className={`rounded font-black tracking-wide ${compactMode ? 'px-0.5' : 'px-1'}`}
                                            style={{ backgroundColor: kindBadge(job.phase_kind).bg, color: kindBadge(job.phase_kind).fg }}
                                            title={`Phase ${job.phase_sequence || ''}: ${job.phase_kind}`}
                                          >
                                            {compactMode ? kindLetter(job.phase_kind) : <><span className="dp-full">{kindBadge(job.phase_kind).label}</span><span className="dp-abbr">{kindLetter(job.phase_kind)}</span></>}
                                          </span>
                                        )}
                                        {siblingInfo && siblingInfo.total > 1 && (
                                          <span className={`bg-white/30 rounded font-bold ${compactMode ? 'px-0.5' : 'px-1'}`} title={`Phase ${siblingInfo.index}/${siblingInfo.total} of this trip`}>
                                            {siblingInfo.index}/{siblingInfo.total}
                                          </span>
                                        )}
                                        {job.packing_service && !job.phase_kind && <span className={`bg-white/30 rounded font-bold ${compactMode ? 'px-0.5' : 'px-1'}`}>{compactMode ? 'P' : 'PACK'}</span>}
                                        {isOutOfState && stateAbbr && <span className={`dp-st bg-white/30 rounded ml-auto font-bold ${compactMode ? 'px-0.5' : 'px-1'}`}>{stateAbbr}</span>}
                                      </div>
                                      {/* Line 2: Client name. First name only at
                                          2wk+ (any mode); full name at 1wk. */}
                                      <div className={`dp-name font-bold truncate ${tvPillRich ? 'text-[10px] leading-tight' : zoomWeeks >= 2 ? 'text-[10px]' : 'text-xs'}`}
                                           title={useFirstNameOnly ? (client?.name || '') : ''}>
                                        {useFirstNameOnly ? (client?.name?.split(' ')[0] || '?') : (client?.name || '?')}
                                      </div>
                                      {/* Line 3: Time + type.
                                          - 1wk: time + TYPE
                                          - 2wk (any mode): time only — the
                                            columns are still ~120px wide so
                                            we can afford this. Previously the
                                            TV layout was stripping the time at
                                            2wk too aggressively.
                                          - 3wk+: hidden (columns too tight). */}
                                      {zoomWeeks < 3 && cellJobs.length <= 2 && (
                                        <div className={`opacity-90 font-semibold ${zoomWeeks >= 2 ? 'text-[9px]' : 'text-[11px]'}`}
                                             title={`${isTentative ? '~ tentative date · ' : ''}${formatTime12(job.time)} · ${(job.job_label || job.type || '').replace(/_/g, ' ').toUpperCase()}`}>
                                          {isTentative ? '~' : ''}{formatTime12(job.time)}
                                          {zoomWeeks < 2 ? <span className="dp-type">{` · ${(job.job_label || job.type || '').replace(/_/g, ' ').toUpperCase()}`}</span> : ''}
                                        </div>
                                      )}
                                      {/* Line 4: Truck assignments.
                                          - 1wk: full chips
                                          - 2wk: full chips — also restored
                                            now that we know columns have room.
                                          - 3wk+: hidden. */}
                                      {zoomWeeks <= 2 && cellJobs.length <= 1 && (job.truck_ids || []).length > 0 && (
                                        <div className={`dp-trucks flex flex-wrap gap-0.5 mt-0.5 ${zoomWeeks >= 2 ? 'text-[10px]' : 'text-xs'}`}>
                                          {job.truck_ids.map(tid => { const truck = getTruck(tid); const conflict = isTruckConflict(tid, dateStr, job.id, job.leader_id); return <span key={tid} className={`dp-truck ${zoomWeeks >= 2 ? 'px-1 py-0.5' : 'px-1.5 py-0.5'} rounded font-semibold ${conflict ? 'bg-red-500 text-white ring-1 ring-red-300' : 'bg-black/20'}`} title={conflict ? `⚠ ${truck?.name} is double-booked on this day` : (truck?.name || '')}><span className="dp-truck-label">{truck?.name}{conflict ? ' ⚠' : ''}</span></span>; })}
                                        </div>
                                      )}
                                    </React.Fragment>
                                    );
                                  })()
                                  )
                                )}
                              </div>
                              {isExpanded && (
                                <DispatchPillExpanded
                                  job={job} client={client} isSpanDay={isSpanDay} dateStr={dateStr} spanDayNum={spanDayNum}
                                  getCrew={getCrew} getTruck={getTruck} getTripTag={getTripTag} getLegCrew={getLegCrew}
                                  estimates={estimates}
                                  allJobs={jobs}
                                  onClose={() => setExpandedPill(null)}
                                  onEditAssignment={(mode) => { setExpandedPill(null); setAssignModal(isSpanDay ? { ...job, _isLegEdit: true, _legDate: dateStr, _assignMode: mode || 'all' } : { ...job, _assignMode: mode || 'all' }); }}
                                  onSendSms={() => { setExpandedPill(null); setSmsConfirm(job); }}
                                  onNavigateToClient={(clientId) => { setExpandedPill(null); nav.navigateToClient(clientId); }}
                                  saveJob={saveJob}
                                  showToast={showToast}
                                  align={dayIdx >= totalDays / 2 ? 'right' : 'left'}
                                />
                              )}
                            </div>
                          );
                        })}

                        {/* HOLD tags */}
                        {cellJobs.filter(j => {
                          if (!j._isSpanDay || getTripTag(j.trip_tags, dateStr) !== 'Hold') return false;
                          const lc = getLegCrew(j.trip_tags, dateStr);
                          if (lc && lc.length > 0) return lc.includes(crewMember.id);
                          const anyLc = j.trip_tags ? Object.values(j.trip_tags).find(v => typeof v === 'object' && v.leg_crew_ids && v.leg_crew_ids.length > 0)?.leg_crew_ids : null;
                          if (anyLc) return anyLc.includes(crewMember.id);
                          return true;
                        }).map(job => {
                          const holdClient = getClient(job.client_id)?.name?.split(' ')[0] || '?';
                          const holdExpanded = expandedPill === job.id + crewMember.id + dateStr;
                          return (
                            <div key={'hold-' + job.id} className="mt-auto pt-1 relative">
                              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] bg-white border border-slate-300 text-slate-600 cursor-pointer hover:bg-slate-100 shadow-sm"
                                onClick={(e) => { e.stopPropagation(); setExpandedPill(holdExpanded ? null : job.id + crewMember.id + dateStr); }}>
                                <span className="font-bold text-slate-600">HOLD</span>
                                <span>- {holdClient}</span>
                              </div>
                              {holdExpanded && (
                                <div className="absolute left-0 top-full z-50 bg-white border border-slate-300 rounded-lg shadow-2xl p-4 w-72 mt-1 text-slate-900" style={{ zIndex: 999 }} onClick={e => e.stopPropagation()}>
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="font-bold text-sm">{getClient(job.client_id)?.name} — Hold</div>
                                    <IconButton label="Close" size="sm" className="-my-1 -mr-1" onClick={() => setExpandedPill(null)}><IconX size={14}/></IconButton>
                                  </div>
                                  <div className="text-xs text-slate-500 mb-2">Items in transit, crew available for other jobs.</div>
                                  <div className="text-xs space-y-1 mb-2">
                                    <div><strong>To:</strong> {job.to_address}</div>
                                    {job.heavy_items && <div><strong>Heavy:</strong> {job.heavy_items}</div>}
                                  </div>
                                  <div className="text-xs mb-2"><strong>Trip Crew:</strong>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {(getLegCrew(job.trip_tags, dateStr) || (job.trip_tags ? (Object.values(job.trip_tags).find(v => typeof v === 'object' && v.leg_crew_ids)?.leg_crew_ids) : null) || []).map(cid => {
                                        const c = getCrew(cid);
                                        return <span key={cid} className="px-2 py-0.5 rounded text-white text-xs" style={{backgroundColor: c?.display_color || '#888'}}>{c?.name}</span>;
                                      })}
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <Button variant="primary" size="sm" onClick={() => { setExpandedPill(null); setAssignModal({ ...job, _isLegEdit: true, _legDate: dateStr, _assignMode: 'crew' }); }}>Crew Assignment</Button>
                                    <Button variant="secondary" size="sm" onClick={() => { setExpandedPill(null); setAssignModal({ ...job, _isLegEdit: true, _legDate: dateStr, _assignMode: 'truck' }); }}>Truck Assignment</Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Ghost holds (pick C): grey chip under the lead who loaded the truck */}
                        {cellGhosts.map(g => renderGhostHold(g, crewMember.id))}
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sidebar: Unassigned panel.
          Hidden in TV layout mode (2wk+ on a large display) because
          the at-a-glance overview doesn't need a drag target, and the
          panel eats ~290px that the day columns can use for legible
          names. The dispatcher uses 1wk on the laptop for unassigned
          drag-and-drop work. */}
      {!isTvLayoutActive && !narrowRail && (
      <div className="ml-6 flex flex-col flex-shrink-0" style={{ width: railW }}>
        {unassignedRail}
      </div>
      )}
      </div>

      {assignModal && <AssignModal job={assignModal} onClose={() => setAssignModal(null)} onSave={handleSaveAssignment} crew={crew} trucks={trucks} clients={clients} jobs={jobs} mode={assignModal._assignMode || 'all'} />}
      {crewReorderModal && <CrewReorderModal crew={crew} onSave={handleSaveCrewOrder} onClose={() => setCrewReorderModal(false)} />}

      {/* Date Change Confirmation Dialog */}
      {dateConfirm && (() => {
        const isNewClient = dateConfirm.isNewClient;
        const isNewEstimate = dateConfirm.isNewEstimate;
        const dcClient = isNewClient ? { name: dateConfirm.clientData._clientName } : isNewEstimate ? { name: dateConfirm.estimateData._clientName } : getClient(dateConfirm.job?.client_id);
        const dcCrew = getCrew(dateConfirm.newCrewId);
        const oldLabel = (() => { try { return format(parseISO(dateConfirm.oldDate), 'EEE MMM d'); } catch { return dateConfirm.oldDate; } })();
        const newLabel = (() => { try { return format(parseISO(dateConfirm.newDate), 'EEE MMM d'); } catch { return dateConfirm.newDate; } })();
        const modalTitle = isNewEstimate ? "Schedule Booked Estimate" : isNewClient ? "Confirm Date & Create Job" : "Confirm Date Change";
        // Moves (2026-09-18): which of the move's days this leg is — the
        // copy names it ("moves the unload day to …") and the Tentative key
        // flips that day's flag after the drop (confirmDateChange).
        const isMoveDrag = !isNewClient && !isNewEstimate;
        const dcKey = isMoveDrag ? jobDateKey(dateConfirm.job, movesById.get(dateConfirm.job?.move_id)) : 'load';
        const dcKindWord = dcKey === 'pack' ? 'pack' : dcKey === 'unload' ? 'unload' : (dateConfirm.job?.phase_kind && dateConfirm.job.phase_kind !== 'move' ? 'load' : 'job');
        return (
          <Modal title={modalTitle} onClose={() => setDateConfirm(null)} width="max-w-md">
            <div className="text-sm text-slate-700 space-y-3">
              <div className={`${isNewEstimate ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'} border rounded-lg p-3`}>
                <div className={`font-bold ${isNewEstimate ? 'text-emerald-800' : 'text-amber-800'} mb-1`}>{isNewEstimate ? 'Scheduling estimate on a different date' : isNewClient ? 'Creating job on a different date than scheduled' : `This moves the ${dcKindWord} day to ${newLabel}.`}</div>
                <div className={`text-xs ${isNewEstimate ? 'text-emerald-700' : 'text-amber-700'}`}>{isNewEstimate ? 'The estimate was quoted for a different date. Confirm to schedule on the new date.' : isNewClient ? 'The client was scheduled for a different date. Confirm to create the job on the new date.' : 'The move\'s date updates everywhere — client file, estimates, board.'}</div>
              </div>
              {isMoveDrag && dateConfirm.job?.move_id && (
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">Date is:</span>
                  <ChoiceKeys size="sm" ariaLabel="New date confirmed or tentative"
                    items={[{ value: 'ok', label: 'Confirmed' }, { value: 'tent', label: '~ Tentative', color: '#b45309' }]}
                    value={dateConfirm.tentative ? 'tent' : 'ok'}
                    onChange={(v) => setDateConfirm(dc => dc ? { ...dc, tentative: v === 'tent' } : dc)} />
                </div>
              )}
              <div className="space-y-1">
                <div><strong>Client:</strong> {dcClient?.name || '?'}</div>
                {isNewEstimate && <div><strong>Estimate:</strong> {dateConfirm.estimateData._jobNumber}</div>}
                {!isNewClient && !isNewEstimate && <div><strong>Job:</strong> {dateConfirm.job?.job_label || dateConfirm.job?.type}</div>}
                <div className="flex items-center gap-2">
                  <span><strong>Scheduled:</strong> {oldLabel}</span>
                  <span className="text-slate-400">→</span>
                  <span className="font-bold text-blue-700">{newLabel}</span>
                </div>
                {dcCrew && <div><strong>Assign to:</strong> {dcCrew.name}</div>}
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4">
              <Button variant="secondary" onClick={() => setDateConfirm(null)}>Cancel</Button>
              <Button variant="primary" onClick={confirmDateChange}>Confirm Move</Button>
            </div>
          </Modal>
        );
      })()}

      {jobModal && <JobModal job={jobModal.id ? jobModal : null} initialOverrides={!jobModal.id ? jobModal : null} onClose={() => { setJobModal(null); setDefaultDate(null); }} onSave={handleSaveJob} clients={clients} crew={crew} trucks={trucks} defaultDate={defaultDate} jobs={jobs} />}
      {quickModal && <QuickDispatchModal client={quickModal.client} initialDate={quickModal.date} onClose={() => setQuickModal(null)} showToast={showToast} />}
      {/* Pill hover card (2026-08-07) — read-only glance: who's on it,
          which truck, when. pointer-events-none so it can never trap the
          mouse; drag + click both clear it. */}
      {hoverPill && !dragJob && (() => {
        const hj = hoverPill.job;
        const hClient = getClient(hj.client_id);
        const hLead = getCrew(hj.leader_id);
        const hLegCrew = hj._isSpanDay ? getLegCrew(hj.trip_tags, hoverPill.dateStr) : null;
        const hCrewIds = (hLegCrew && hLegCrew.length > 0 ? hLegCrew : (hj.crew_ids || []));
        const hCrewNames = hCrewIds.filter(id => id !== hj.leader_id).map(id => getCrew(id)?.name).filter(Boolean);
        const hTrucks = (hj.truck_ids || []).map(tid => getTruck(tid)?.name).filter(Boolean);
        const hPhase = hj.phase_kind ? kindBadge(hj.phase_kind).label : (hj.job_label || hj.type || '').replace(/_/g, ' ');
        // Walkthroughs: no truck, and the row's `duration` is just the DB
        // default (4) — meaningless for a walkthrough, so no "Est Xh"
        // (Jaylen 2026-08-07). Completion lives on the client.
        const hIsWt = hj.type === 'WALKTHROUGH';
        const hWtDone = hIsWt && (hClient?.walkthrough_status === 'completed' || hj.status === 'completed');
        // Unassigned (2026-09-25): no crew to list — show where it is and
        // what the estimate needs, which is what the dispatcher is deciding.
        const hUnassigned = !hIsWt && (hj.status === 'unassigned' || !(hj.crew_ids || []).length);
        const hEst = hUnassigned ? (estimates || []).find(e => e.id === hj.estimate_id) : null;
        const hNeeds = hEst ? [hEst.crew_size_needed ? `${hEst.crew_size_needed} movers` : '', hEst.trucks_needed && hEst.trucks_needed !== 'none' ? `${truckSizeShortLabel(hEst.trucks_needed)} truck${estimateExtraTrucks(hEst).length ? ` + ${estimateExtraTrucks(hEst).length} extra` : ''}` : ''].filter(Boolean).join(' · ') : '';
        const hFrom = (hj.from_address || hClient?.origin_address || '').split(',').slice(0, 2).join(',');
        const hTo = (hj.to_address || hClient?.destination_address || '').split(',').slice(0, 2).join(',');
        return (
          <div className="fixed z-[120] w-64 rounded-xl bg-slate-900/95 text-white shadow-2xl border border-slate-700 px-3.5 py-3 pointer-events-none"
            style={{ left: hoverPill.x, top: hoverPill.y }}>
            <div className="font-bold text-sm truncate">{hClient?.name || 'Unknown'}</div>
            <div className="text-[11px] text-slate-300 mt-0.5">
              {hPhase}{hj.time ? ` · ${formatTime12(hj.time)}` : ''}{!hIsWt && hj.duration ? ` · Est ${hj.duration}h` : ''}
            </div>
            {hUnassigned ? (
              <div className="mt-2 space-y-1 text-[11px]">
                {hFrom && <div className="truncate"><span className="text-slate-400 font-semibold">From:</span> {hFrom}</div>}
                {hTo && <div className="truncate"><span className="text-slate-400 font-semibold">To:</span> {hTo}</div>}
                <div><span className="text-slate-400 font-semibold">Needs:</span> {hNeeds || '—'}</div>
              </div>
            ) : (
            <div className="mt-2 space-y-1 text-[11px]">
              <div><span className="text-slate-400 font-semibold">{hIsWt ? 'Estimator:' : 'Lead:'}</span> <span className="font-bold" style={{ color: hLead?.display_color || '#fff' }}>{hLead?.name || '—'}</span></div>
              {!hIsWt && <div><span className="text-slate-400 font-semibold">Crew:</span> {hCrewNames.length ? hCrewNames.join(', ') : '—'}</div>}
              {!hIsWt && <div><span className="text-slate-400 font-semibold">Truck:</span> {hTrucks.length ? hTrucks.join(' + ') : '—'}</div>}
            </div>
            )}
            <div className="mt-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">
              {hWtDone ? '✓ Walkthrough completed' : hUnassigned ? 'Unassigned · click to assign' : `${(hj.status || '').replace(/_/g, ' ')} · click pill for actions`}
            </div>
          </div>
        );
      })()}
      {pastWtModal && (
        <Modal title="Past Walkthroughs — Resolve" onClose={() => setPastWtModal(false)} width="max-w-lg">
          <p className="text-xs text-slate-500 mb-3">These walkthrough appointments came and went with no outcome recorded. If it happened — any of you can have done it, no assignment needed — one click closes it out everywhere. If it fell through, reschedule it.</p>
          {pastWtJobs.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">All resolved — nothing past due. 🎉</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {pastWtJobs.map(j => {
                const cl = getClient(j.client_id);
                return (
                  <div key={j.id} className="flex items-center gap-3 border border-slate-200 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm text-slate-800 truncate">{cl?.name || 'Unknown client'}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {format(parseISO(j.move_date), 'EEE, MMM d')}{j.time ? ` · ${formatTime12(j.time)}` : ''}{j.from_address ? ` · ${j.from_address.split(',')[0]}` : ''}
                      </div>
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => resolvePastWalkthrough(j)}
                      title="Mark the walkthrough completed (job + client)">
                      ✓ It Happened
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => { setPastWtModal(false); setJobModal(j); }}>
                      Reschedule
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}
      {portalPreview && (
        <Modal title="Preview a Crew Portal" onClose={() => setPortalPreview(null)} width="max-w-md">
          <div className="space-y-4">
            <p className="text-xs text-slate-500">Opens a read-only copy of the portal in a new tab — exactly what that crew member sees on their phone for the chosen day. Nothing you do in a preview can submit recaps or waivers.</p>
            <FormField label="Crew Member">
              <div className="flex gap-1.5 flex-wrap max-h-44 overflow-y-auto">
                {activeCrew.map(m => (
                  <button key={m.id} type="button" onClick={() => setPortalPreview(p => ({ ...p, crewId: m.id }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${portalPreview.crewId === m.id ? 'text-white border-transparent' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                    style={portalPreview.crewId === m.id ? { background: m.display_color || '#0d9488' } : undefined}>
                    {m.name}
                  </button>
                ))}
              </div>
            </FormField>
            <FormField label="Day">
              <DatePicker className="w-full" value={portalPreview.date} onChange={v => setPortalPreview(p => ({ ...p, date: v }))} placeholder="Select day…" />
            </FormField>
            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={!portalPreview.crewId}
                onClick={() => { openPortalPreview(portalPreview.crewId, portalPreview.date); setPortalPreview(null); }}>
                👁 Open Preview
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* SMS Confirm */}
      {smsConfirm && (() => {
        const smsJob = smsConfirm;
        const smsClient = getClient(smsJob.client_id);
        const smsCrewList = (smsJob.crew_ids || []).map(id => {
          const m = getCrew(id);
          return { id, name: m?.name || 'Unknown', phone: m?.phone, isLeader: id === smsJob.leader_id };
        });
        const withPhone = smsCrewList.filter(c => c.phone);
        const noPhone = smsCrewList.filter(c => !c.phone);
        return (
          <Modal title="Dispatch This Job" onClose={() => setSmsConfirm(null)}>
            <div className="space-y-4">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <div className="font-medium text-purple-900">{smsClient?.name || 'Unknown Client'}</div>
                <div className="text-sm text-purple-700">{smsJob.move_date} @ {smsJob.time || 'TBD'} — {smsJob.type}</div>
              </div>
              <div>
                <div className="text-sm font-medium text-slate-700 mb-2">Will send SMS to ({withPhone.length}):</div>
                <div className="space-y-1">
                  {withPhone.map(c => (
                    <div key={c.id} className="flex items-center gap-2 text-sm">
                      <span className="text-green-500">✓</span>
                      <span className="font-medium">{c.name}</span>
                      {c.isLeader && <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Lead — Full Details</span>}
                      {!c.isLeader && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Schedule Only</span>}
                      <span className="text-xs text-slate-400">{c.phone}</span>
                    </div>
                  ))}
                </div>
              </div>
              {noPhone.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-2">
                  <div className="text-xs text-yellow-800 font-medium">⚠️ No phone number ({noPhone.length}):</div>
                  <div className="text-xs text-yellow-700">{noPhone.map(c => c.name).join(', ')}</div>
                </div>
              )}
              {withPhone.length === 0 && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
                  No crew members have phone numbers. Add phone numbers in Crew Management first.
                </div>
              )}
              <div className="flex flex-wrap justify-end items-center gap-3 pt-2">
                <Button variant="secondary" onClick={() => setSmsConfirm(null)}>Cancel</Button>
                {/* Status-only path — the old green pill button's
                    behavior, kept as an explicit escape hatch ("crew
                    already knows, just mark it"). Doesn't clear
                    change-log entries: nobody was notified. */}
                <Button
                  variant="tertiary"
                  disabled={smsSending}
                  onClick={async () => {
                    try {
                      await saveJob({ ...smsJob, status: 'dispatched' });
                      setSmsConfirm(null);
                      showToast('Marked dispatched — no text sent');
                    } catch (e) {
                      showToast('Error: ' + e.message, 'error');
                    }
                  }}
                >
                  Mark dispatched without texting
                </Button>
                <Button
                  variant="success"
                  loading={smsSending}
                  disabled={withPhone.length === 0}
                  onClick={async () => {
                    setSmsSending(true);
                    try {
                      const result = await dispatchJobSMS(smsJob, clients, crew, trucks);
                      await saveJob({ ...smsJob, status: 'dispatched' });
                      // This send covers any changes-since-send entries
                      // for THIS job (sql/074) — its crew just got the
                      // fresh details.
                      try {
                        await db.clearDispatchChanges({ jobId: smsJob.id, reason: 'job_send' });
                        refreshDispatchGate();
                      } catch (clearErr) {
                        console.warn('clearDispatchChanges (job_send) failed:', clearErr?.message || clearErr);
                      }
                      setSmsConfirm(null);
                      showToast(`SMS sent to ${result.sent} crew member${result.sent !== 1 ? 's' : ''}${result.failed ? ` (${result.failed} failed)` : ''}`);
                    } catch (e) {
                      showToast('SMS Error: ' + e.message, 'error');
                    } finally {
                      setSmsSending(false);
                    }
                  }}
                >
                  📱 Text {withPhone.length} Crew & Dispatch
                </Button>
              </div>
            </div>
          </Modal>
        );
      })()}

      {/* Quick Text */}
      {quickText && (
        <Modal title="Quick Text" onClose={() => setQuickText(null)}>
          <div className="space-y-4">
            <FormField label="Send to">
              <Combobox
                items={crew.filter(c => c.active !== false && c.phone).map(c => ({ value: c.id, label: c.name, sub: c.phone }))}
                value={quickText.crewId}
                onChange={v => setQuickText(prev => ({ ...prev, crewId: v }))}
                placeholder="Select crew member..."
              />
            </FormField>
            <FormField label="Message">
              <textarea className={buttonStyles.inputCls} rows="4" value={quickText.message} onChange={e => setQuickText(prev => ({ ...prev, message: e.target.value }))} placeholder="Schedule change, heads up, etc..." />
            </FormField>
            {quickText.crewId && quickText.message && (
              <pre className="p-3 bg-slate-900 text-green-400 text-xs rounded-lg whitespace-pre-wrap font-mono">BEATY MOVING{'\n\n'}{quickText.message}</pre>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setQuickText(null)}>Cancel</Button>
              <Button
                variant="primary"
                loading={smsSending}
                disabled={!quickText.crewId || !quickText.message}
                onClick={async () => {
                  setSmsSending(true);
                  try {
                    const member = crew.find(c => c.id === quickText.crewId);
                    const body = `BEATY MOVING\n\n${quickText.message}`;
                    const response = await fetch('/api/send-sms', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ to: member.phone, body }),
                    });
                    const data = await response.json();
                    if (!response.ok) throw new Error(data.error || 'Failed');
                    setQuickText(null);
                    showToast(`Text sent to ${member.name}`);
                  } catch (e) { showToast('Error: ' + e.message, 'error'); }
                  finally { setSmsSending(false); }
                }}
              >
                💬 Send Text
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {historyModal && (
        <HistoryModal
          onClose={() => setHistoryModal(false)}
          onRestore={async (date) => { await restoreSnapshot(date); showToast('Board restored from ' + date); }}
          getSnapshots={getSnapshots}
        />
      )}

      {confirmElement}

      {dailyModal && (
        <DailyDispatchModal
          dateStr={dailyModal}
          crew={crew}
          jobs={jobs}
          clients={clients}
          trucks={trucks}
          onClose={() => setDailyModal(null)}
          onSent={(msg) => { setDailyModal(null); showToast(msg); refreshDispatchGate(); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

      {/* Changes-since-send panel — opens from a day header's amber
          "↻ N changed" chip (sql/074). */}
      {changesModal && (
        <DispatchChangesModal
          dateStr={changesModal}
          jobs={jobs}
          clients={clients}
          crew={crew}
          trucks={trucks}
          onClose={() => { setChangesModal(null); refreshDispatchGate(); }}
          onResend={(d) => { setChangesModal(null); setDailyModal(d); }}
          onTextJob={(job) => { setChangesModal(null); setSmsConfirm(job); }}
          onChanged={refreshDispatchGate}
        />
      )}

      {weeklyModal && (
        <WeeklyScheduleModal
          crew={crew}
          weekDates={fullWeekDates}
          weekStart={weekStart}
          jobs={jobs}
          availability={availability}
          clients={clients}
          trucks={trucks}
          onClose={() => setWeeklyModal(false)}
          onSent={(msg) => { setWeeklyModal(false); showToast(msg); }}
          onError={(msg) => showToast(msg, 'error')}
        />
      )}

    </div>
  );
}
